<template>
  <div class="dashboard-page supervisor-dashboard supervisor-dashboard-root admin-dashboard-root">

    <!-- =====================================================
     PAGE HEADER
====================================================== -->
    <div class="dashboard-sticky-area">
      <div class="dashboard-topbar">
        <h4 class="dashboard-page-title">
          <CIcon name="cil-speedometer" class="dashboard-title-icon" />
          {{ $t("nav.dashboard") }}
        </h4>

        <div class="dashboard-header-right">
          <!-- Global Dashboard Search -->
          <div class="dashboard-search-box" ref="searchBoxRef">
            <CIcon name="cil-search" class="search-icon" />
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="dashboardText('ค้นหา...', 'Search...')"
              class="dashboard-search-input"
              @keydown="onKeyboardSearch" v-listbox-escape="closeKeyboardSearch"
              role="combobox" aria-autocomplete="list" :aria-controls="keyboardSearchId"
              :aria-expanded="String(Boolean(searchQuery && showSearchDropdown))"
              :aria-activedescendant="searchQuery && showSearchDropdown && keyboardSearchIndex >= 0 ? keyboardSearchId + '-' + keyboardSearchIndex : null"
              :aria-label="$t('common.search')"
              @focus="showSearchDropdown = true"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="search-clear-btn" :aria-label="$t('common.clearSearch')"
              @click="clearSearch"
            >
              <CIcon name="cil-x" />
            </button>

            <!-- Search Results Dropdown List -->
            <div
              v-if="searchQuery && showSearchDropdown"
              class="search-dropdown-menu" :id="keyboardSearchId" role="listbox" :aria-label="$t('common.search')"
            >
              <div class="search-dropdown-header">
                <span>{{ dashboardText('ผลการค้นหา (' + searchMatches.length + ' รายการ)', 'Search Results (' + searchMatches.length + ')') }}</span>
                <small v-if="searchMatches.length">{{ dashboardText('คลิกเพื่อไปยังรายการ', 'Click to jump to item') }}</small>
              </div>

              <div v-if="!searchMatches.length" class="p-3 text-center text-muted small">
                {{ dashboardText('ไม่พบข้อมูลที่ตรงกัน (ค้นหาได้ทั้งรหัสอาคาร, ชื่อไทย-อังกฤษ, ห้อง, หรือเลขที่แจ้ง)', 'No matches found (search by building, TH/EN name, room, or ticket #)') }}
              </div>

              <div
                v-for="(m, idx) in searchMatches"
                :key="m.id + '-' + idx"
                class="search-dropdown-item keyboard-listbox-option" :id="keyboardSearchId + '-' + idx" role="option" :aria-selected="String(keyboardSearchIndex === idx)" :data-keyboard-active="keyboardSearchIndex === idx" @mousedown.prevent
                @click="onSelectSearchResult(m)"
              >
                <span class="item-badge" :class="m.badgeClass">{{ m.sectionLabel }}</span>
                <div class="item-content">
                  <div class="item-title">{{ m.title }}</div>
                  <div class="item-desc">{{ m.description }}</div>
                  <small v-if="m.meta" class="text-muted d-block mt-1">{{ m.meta }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Range Filter Hotkeys (Right-aligned) -->
          <div class="dashboard-filter">
            <div v-if="selectedRange === 'custom'" class="custom-date-range">
              <input type="date" v-model="startDate" @change="onCustomDateChange" />
              <span>-</span>
              <input type="date" v-model="endDate" @change="onCustomDateChange" />
            </div>

            <div class="range-tabs">
              <button v-for="range in rangeOptions" :key="range.value" type="button" class="range-tab" :class="{
                active: selectedRange === range.value,
                'filter-tab': range.value === 'custom'
              }" @click="onRangeSelect(range.value)">
                <CIcon v-if="range.value === 'custom'" name="cil-filter" />
                <span v-else>{{ range.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         KPI SUMMARY
    ====================================================== -->
    <div class="kpi-grid mb-3">
      <!-- Total -->
      <div class="summary-card" @click="$router.push('/supervisor/mainsupervisor')" v-keyboard-activate role="button" tabindex="0">
        <div class="summary-card-top">
          <div class="summary-icon icon-blue"><CIcon name="cil-bell" /></div>
        </div>
        <div class="summary-label">{{ $t("dashboard.kpiTotalRequests") }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.total }}</div>
        </div>
      </div>

      <!-- In Progress -->
      <div class="summary-card" @click="$router.push({ path: '/supervisor/mainsupervisor', query: { status: 'active' } })" v-keyboard-activate role="button" tabindex="0">
        <div class="summary-card-top">
          <div class="summary-icon icon-orange">
            <CIcon name="cil-av-timer" />
          </div>
        </div>
        <div class="summary-label">{{ $t("dashboard.kpiInProgress") }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.inProgress }}</div>
        </div>
      </div>

      <!-- Overdue -->
      <div class="summary-card" @click="$router.push({ path: '/supervisor/mainsupervisor', query: { status: 'overdue' } })" v-keyboard-activate role="button" tabindex="0">
        <div class="summary-card-top">
          <div class="summary-icon icon-red">
            <CIcon name="cil-clock" />
          </div>
        </div>
        <div class="summary-label">{{ $t("dashboard.kpiOverSla") }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.overdue }}</div>
        </div>
      </div>

      <!-- Completed -->
      <div class="summary-card" @click="$router.push('/supervisor/history')" v-keyboard-activate role="button" tabindex="0">
        <div class="summary-card-top">
          <div class="summary-icon icon-green">
            <CIcon name="cil-check-circle" />
          </div>
        </div>
        <div class="summary-label">{{ $t("dashboard.kpiCompleted") }}</div>
        <div class="summary-bottom">
          <div class="summary-value">{{ stats.completed }}</div>
        </div>
      </div>

      <!-- Technician Availability -->
      <div
        class="summary-card"
        @click="goToTechnicianWorkload"
        v-keyboard-activate
        role="button"
        tabindex="0"
        :title="dashboardText('คลิกเพื่อดูภาระงานช่างเทคนิค', 'Click to view technician workload')"
      >
        <div class="summary-card-top">
          <div class="summary-icon icon-indigo">
            <CIcon name="cil-people" />
          </div>
          <div class="summary-tech-pills">
            <span class="tech-pill pill-free" :title="dashboardText('ช่างว่าง', 'Available technicians')">
              {{ technicianAvailabilitySummary.available }} {{ dashboardText('ว่าง', 'Free') }}
            </span>
            <span class="tech-pill pill-busy ml-1" :title="dashboardText('กำลังซ่อม', 'Busy technicians')">
              {{ technicianAvailabilitySummary.busy }} {{ dashboardText('ซ่อม', 'Busy') }}
            </span>
          </div>
        </div>
        <div class="summary-label">{{ dashboardText('สถานะช่าง', 'Technician Status') }}</div>
        <div class="summary-bottom">
          <div class="summary-value">
            {{ technicianAvailabilitySummary.total }}
            <small class="text-muted font-weight-normal ml-1">{{ dashboardText('คน', 'techs') }}</small>
          </div>
        </div>
      </div>
    </div>

    <CModal
      :show.sync="showPartsDetailModal"
      class="parts-detail-modal"
      centered
      size="lg"
    >
      <template #header>
        <div class="parts-modal-header d-flex justify-content-between align-items-center w-100">
          <div>
            <h5 class="parts-modal-title mb-1 font-weight-bold">
              {{ dashboardText('รายละเอียดอะไหล่ / อุปกรณ์', 'Parts & Equipment Details') }}
            </h5>
            <p class="parts-modal-subtitle mb-0 text-muted">
              {{ dashboardText('ข้อมูลรายการอะไหล่และค่าใช้จ่ายของใบงาน', 'Parts breakdown and itemized costs') }}
            </p>
          </div>
          <button type="button" class="parts-modal-close-btn" @click="showPartsDetailModal = false" aria-label="Close">
            &times;
          </button>
        </div>
      </template>

      <div v-if="selectedPartsTicket" class="parts-detail-content">
        <!-- Ticket Info Strip -->
        <div class="parts-modal-info-bar">
          <div class="parts-info-col">
            <span class="parts-info-label">{{ dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.') }}</span>
            <strong class="parts-info-value ticket-id">{{ selectedPartsTicket.number }}</strong>
          </div>
          <div class="parts-info-col">
            <span class="parts-info-label">{{ dashboardText('ประเภทงาน', 'Job Type') }}</span>
            <span class="parts-info-value">{{ selectedPartsTicket.jobType }}</span>
          </div>
          <div class="parts-info-col text-right">
            <span class="parts-info-label">{{ dashboardText('จำนวนรายการ', 'Total Items') }}</span>
            <span class="parts-info-value">{{ selectedPartsTicket.parts.length }} {{ dashboardText('รายการ', 'items') }}</span>
          </div>
        </div>

        <!-- Parts Table in Modal (when parts exist) -->
        <div v-if="selectedPartsTicket.parts.length" class="parts-modal-table-container">
          <table class="parts-modal-table">
            <thead>
              <tr>
                <th class="th-num">#</th>
                <th class="th-name">{{ dashboardText('รายการอะไหล่ / อุปกรณ์', 'Part / Equipment Name') }}</th>
                <th class="th-qty text-center">{{ dashboardText('จำนวน', 'Qty') }}</th>
                <th class="th-price text-right">{{ dashboardText('ราคาต่อหน่วย (บาท)', 'Unit Price (THB)') }}</th>
                <th class="th-total text-right">{{ dashboardText('รวม (บาท)', 'Total (THB)') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(part, index) in selectedPartsTicket.parts" :key="selectedPartsTicket.id + '-modal-part-' + index">
                <td class="td-num">{{ index + 1 }}</td>
                <td class="td-name font-weight-bold">{{ part.name }}</td>
                <td class="td-qty text-center">{{ part.quantity }}</td>
                <td class="td-price text-right">{{ formatCurrency(part.unitPrice) }}</td>
                <td class="td-total text-right font-weight-bold">{{ formatCurrency(part.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State (No parts used) -->
        <div v-else class="parts-modal-empty-state">
          <p class="mb-0 text-muted">{{ dashboardText('ไม่มีการใช้อะไหล่สำหรับงานนี้', 'No parts used for this ticket') }}</p>
        </div>

        <!-- Total Cost Bar -->
        <div class="parts-modal-summary-card">
          <span class="parts-summary-title font-weight-bold">
            {{ dashboardText('ยอดรวมค่าอะไหล่ทั้งสิ้น', 'Total Parts Cost') }}
          </span>
          <div class="parts-summary-amount">
            <strong>{{ formatCurrency(selectedPartsTicket.total) }}</strong>
            <span class="parts-currency-unit">{{ dashboardText('บาท', 'THB') }}</span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="w-100 d-flex justify-content-end">
          <button type="button" class="parts-modal-close-action-btn" @click="showPartsDetailModal = false">
            {{ dashboardText('ปิด', 'Close') }}
          </button>
        </div>
      </template>
    </CModal>



    <!-- =====================================================
         BOTTOM SECTIONS: DESKTOP ONLY (HIDDEN ON MOBILE)
    ====================================================== -->
    <div class="d-none d-md-block">
      <!-- =====================================================
           MAIN DASHBOARD NAVIGATION TABS (ZERO-SCROLL HUBS)
      ====================================================== -->
      <div class="dashboard-main-tabs mb-3">
      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'todo' }"
        @click="activeDashboardTab = 'todo'"
      >
        <CIcon name="cil-list" class="mr-1" />
        {{ dashboardText('งานค้าง/รอจัดการ', 'To-Do Jobs') }}
        <span class="tab-badge" v-if="todoList.length">{{ todoList.length }}</span>
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'workload' }"
        @click="activeDashboardTab = 'workload'"
      >
        <CIcon name="cil-people" class="mr-1" />
        {{ dashboardText('ภาระงานทีม', 'Team Workload') }}
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'analytics' }"
        @click="activeDashboardTab = 'analytics'"
      >
        <CIcon name="cil-chart-pie" class="mr-1" />
        {{ dashboardText('สถิติ & กราฟ', 'Analytics') }}
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'parts' }"
        @click="activeDashboardTab = 'parts'"
      >
        <CIcon name="cil-wrench" class="mr-1" />
        {{ dashboardText('อะไหล่ & ใบงาน', 'Parts & Tickets') }}
      </button>

      <button
        type="button"
        class="main-tab-btn"
        :class="{ active: activeDashboardTab === 'all' }"
        @click="activeDashboardTab = 'all'"
      >
        <CIcon name="cil-grid" class="mr-1" />
        {{ dashboardText('แสดงทั้งหมด', 'Show All') }}
      </button>
    </div>

    <!-- =====================================================
         WORK AREA: TO-DO LIST & SELECTED JOB DETAILS
    ====================================================== -->
    <div v-if="['todo', 'all'].includes(activeDashboardTab)" class="sup-work-layout mb-3" :class="{ 'sup-work-layout-full': !selectedTodo }">

      <!-- To-do List Card -->
      <div class="sup-work-card">

        <div class="sup-work-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div class="d-flex align-items-center gap-2">
              <h4 class="m-0 font-weight-bold">
                {{ dashboardText('งานที่ต้องจัดการ', 'To-do List') }}
              </h4>
            </div>
            <p class="m-0 sup-header-sub text-muted">
              {{ dashboardText('รายการใบแจ้งซ่อมที่รอการตรวจสอบและมอบหมายช่าง', 'Jobs awaiting triage and technician assignment') }}
            </p>
          </div>

          <button type="button" class="btn btn-sm btn-outline-primary sup-view-all-btn font-weight-bold" @click="$router.push('/supervisor/mainsupervisor')">
            {{ dashboardText('ดูทั้งหมด', 'View all') }}
            <CIcon name="cil-arrow-right" class="ml-1" size="sm" />
          </button>
        </div>

        <div v-if="todoList.length" class="task-table-wrapper">
          <div class="sup-task-table d-none d-md-block">

            <div class="sup-task-table-header">
              <div>{{ dashboardText('เลขที่แจ้งซ่อม', 'Ticket') }}</div>
              <div>{{ dashboardText('ผู้แจ้ง', 'Reporter') }}</div>
              <div>{{ dashboardText('รายละเอียดปัญหา', 'Description') }}</div>
              <div>{{ dashboardText('สถานะ & SLA', 'Status & SLA') }}</div>
              <div></div>
            </div>

            <div
              v-for="task in paginatedTodoTickets"
              :key="task._id"
              :id="'todo-' + (task._id || task.ticketNumber)"
              class="sup-task-table-row"
              :class="{
                selected: selectedTodo && selectedTodo._id === task._id
              }"
              @click="selectTodo(task)"
            >
              <!-- Ticket Column -->
              <div class="sup-ticket-col">
                <div class="sup-ticket-num">{{ task.ticketNumber || "-" }}</div>
                <small class="sup-ticket-date">
                  {{
                    formatDate(
                      task.userPart && task.userPart.dateTimeInformed
                        ? task.userPart.dateTimeInformed
                        : task.createdAt
                    )
                  }}
                </small>
              </div>

              <!-- Reporter Column -->
              <div class="sup-reporter-col">
                <div class="sup-reporter-avatar">
                  <CIcon name="cil-user" />
                </div>
                <div class="sup-reporter-info">
                  <span class="sup-reporter-name">{{ getReporterName(task) }}</span>
                  <small class="sup-reporter-dept" v-if="getReporterDepartment(task) !== '-'">{{ getReporterDepartment(task) }}</small>
                </div>
              </div>

              <!-- Problem & Location Column -->
              <div class="sup-problem-col">
                <div class="sup-problem-text" :title="task.userPart && task.userPart.problemDetails ? task.userPart.problemDetails : '-'">
                  {{
                    task.userPart && task.userPart.problemDetails
                      ? task.userPart.problemDetails
                      : "-"
                  }}
                </div>
                <small class="sup-problem-loc" v-if="getTaskLocation(task) !== '-'">
                  <CIcon name="cil-location-pin" class="mr-1 sup-loc-icon" />
                  {{ getTaskLocation(task) }}
                </small>
              </div>

              <!-- Status & SLA Column -->
              <div class="sup-status-col">
                <div class="sup-status-badges">
                  <span class="soft-status" :class="task.status === 'pending' ? 'soft-status-pending' : 'soft-status-active'">
                    {{ getStatusLabel(task.status) }}
                  </span>
                  <span class="sla-countdown-badge" :class="getSlaCountdown(task).className">
                    {{ getSlaCountdown(task).text }}
                  </span>
                </div>
              </div>

              <!-- Action Chevron -->
              <div class="sup-action-col">
                <button type="button" class="sup-row-chevron" :class="{ 'is-selected': selectedTodo && selectedTodo._id === task._id }">
                  <CIcon name="cil-chevron-right" />
                </button>
              </div>

            </div>
          </div>

          <!-- Mobile Ticket Cards (media max-width: 768px) -->
          <div class="sup-mobile-todo-section d-md-none">
            <!-- Filter Pills -->
            <div class="sup-mobile-todo-filter">
              <button
                type="button"
                class="sup-filter-chip"
                :class="{ active: mobileTodoFilter === 'in_progress' }"
                @click="setMobileTodoFilter('in_progress')"
              >
                {{ dashboardText('กำลังดำเนินการ', 'In Progress') }}
                <span class="chip-count">{{ inProgressTodoCount }}</span>
              </button>
              <button
                type="button"
                class="sup-filter-chip"
                :class="{ active: mobileTodoFilter === 'pending' }"
                @click="setMobileTodoFilter('pending')"
              >
                {{ dashboardText('รอรับงาน', 'Pending') }}
                <span class="chip-count">{{ pendingTodoCount }}</span>
              </button>
              <button
                type="button"
                class="sup-filter-chip"
                :class="{ active: mobileTodoFilter === 'all' }"
                @click="setMobileTodoFilter('all')"
              >
                {{ dashboardText('ทั้งหมด', 'All') }}
                <span class="chip-count">{{ todoList.length }}</span>
              </button>
            </div>

            <!-- Impeccable Mobile Cards List -->
            <div v-if="paginatedMobileTodoTickets.length" class="sup-mobile-card-list">
              <div
                v-for="task in paginatedMobileTodoTickets"
                :key="'m-todo-' + task._id"
                :id="'m-todo-' + (task._id || task.ticketNumber)"
                class="sup-mobile-card"
                :class="{ 'is-selected': selectedTodo && selectedTodo._id === task._id }"
                @click="selectTodo(task)"
              >
                <!-- Top Row: Ticket ID + Date + Soft Status -->
                <div class="sup-m-card-header">
                  <div class="sup-m-ticket-group">
                    <span class="sup-m-ticket-num">{{ task.ticketNumber || "-" }}</span>
                    <span class="sup-m-ticket-date">
                      {{ formatDate(task.userPart && task.userPart.dateTimeInformed ? task.userPart.dateTimeInformed : task.createdAt) }}
                    </span>
                  </div>
                  <span class="soft-status" :class="task.status === 'pending' ? 'soft-status-pending' : 'soft-status-active'">
                    {{ getStatusLabel(task.status) }}
                  </span>
                </div>

                <!-- Problem Description -->
                <div class="sup-m-problem-title">
                  {{ task.userPart && task.userPart.problemDetails ? task.userPart.problemDetails : '-' }}
                </div>

                <!-- Meta Micro-Chips -->
                <div class="sup-m-meta-row">
                  <span class="sup-m-chip" v-if="getTaskLocation(task) !== '-'">
                    <CIcon name="cil-location-pin" class="sup-m-chip-icon" />
                    {{ getTaskLocation(task) }}
                  </span>
                  <span class="sup-m-chip" v-if="getTaskCategory(task) !== '-'">
                    <CIcon name="cil-tag" class="sup-m-chip-icon" />
                    {{ getTaskCategory(task) }}
                  </span>
                  <span class="sup-m-chip" v-if="getReporterName(task) !== '-'">
                    <CIcon name="cil-user" class="sup-m-chip-icon" />
                    {{ getReporterName(task) }}
                  </span>
                </div>

                <!-- Direct Action -->
                <div class="sup-m-footer">
                  <button type="button" class="sup-m-assign-btn" @click.stop="goToTicketDetail(task)">
                    <span>{{ dashboardText('ดูรายละเอียด & จัดการ', 'View & Manage') }}</span>
                    <CIcon name="cil-arrow-right" size="sm" />
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="task-empty py-4 text-center">
              <CIcon name="cil-inbox" size="xl" class="mb-2 text-muted" />
              <div class="text-muted small">
                {{ dashboardText('ไม่พบงานในสถานะนี้', 'No tasks in this status') }}
              </div>
            </div>

            <!-- Mobile Pagination -->
            <div v-if="mobileTodoTotalPages > 1" class="todo-pagination mt-3">
              <button type="button" class="todo-page-button" :disabled="mobileTodoCurrentPage === 1" @click="mobileTodoCurrentPage = 1">«</button>
              <button type="button" class="todo-page-button" :disabled="mobileTodoCurrentPage === 1" @click="mobileTodoCurrentPage--">‹</button>
              <button v-for="page in mobileTodoPageNumbers" :key="page" type="button" class="todo-page-button" :class="{ active: mobileTodoCurrentPage === page }" @click="mobileTodoCurrentPage = page">{{ page }}</button>
              <button type="button" class="todo-page-button" :disabled="mobileTodoCurrentPage === mobileTodoTotalPages" @click="mobileTodoCurrentPage++">›</button>
              <button type="button" class="todo-page-button" :disabled="mobileTodoCurrentPage === mobileTodoTotalPages" @click="mobileTodoCurrentPage = mobileTodoTotalPages">»</button>
            </div>
          </div>

          <!-- Desktop Pagination -->
          <div v-if="todoTotalPages > 1" class="todo-pagination d-none d-md-flex">
            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === 1"
              @click="todoCurrentPage = 1">
              «
            </button>

            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === 1" @click="todoCurrentPage--">
              ‹
            </button>

            <button v-for="page in todoPageNumbers" :key="page" type="button" class="todo-page-button"
              :class="{ active: todoCurrentPage === page }" @click="todoCurrentPage = page">
              {{ page }}
            </button>

            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === todoTotalPages"
              @click="todoCurrentPage++">
              ›
            </button>

            <button type="button" class="todo-page-button" :disabled="todoCurrentPage === todoTotalPages"
              @click="todoCurrentPage = todoTotalPages">
              »
            </button>
          </div>

        </div>

        <div v-else class="task-empty">
          <CIcon name="cil-inbox" size="2xl" class="mb-2" />
          <div>
            {{ dashboardText('ไม่มีงานค้างรอจัดการ', 'No pending tasks') }}
          </div>
        </div>

      </div>

      <!-- Job Details Card -->
      <div v-if="selectedTodo" class="sup-work-card sup-detail-card">
        <!-- Header: Clean White Card Header with Accent -->
        <div class="sup-detail-header d-flex justify-content-between align-items-center">
          <div class="sup-detail-header-left">
            <span class="sup-detail-eyebrow">{{ dashboardText('รายละเอียดงานที่เลือก', 'Job Details') }}</span>
            <h4 class="sup-detail-ticket-id">{{ selectedTodo.ticketNumber }}</h4>
          </div>
          <div class="sup-detail-header-right d-flex align-items-center">
            <span class="soft-status" :class="selectedTodo.status === 'pending' ? 'soft-status-pending' : 'soft-status-active'">
              {{ getStatusLabel(selectedTodo.status) }}
            </span>
            <button type="button" class="sup-close-detail-btn ml-3" @click="selectedTodo = null" :title="dashboardText('ปิดรายละเอียด', 'Close Details')">
              <CIcon name="cil-x" />
            </button>
          </div>
        </div>

        <div class="sup-detail-body">
          <!-- 2x2 Meta Grid -->
          <div class="sup-detail-grid">
            <div class="sup-meta-box">
              <span class="sup-meta-label">{{ dashboardText('ผู้แจ้ง', 'Requester') }}</span>
              <strong class="sup-meta-val">{{ getReporterName(selectedTodo) }}</strong>
            </div>

            <div class="sup-meta-box">
              <span class="sup-meta-label">{{ dashboardText('สังกัด', 'Affiliation') }}</span>
              <strong class="sup-meta-val">{{ getTicketReporterAffiliation(selectedTodo) }}</strong>
            </div>

            <div class="sup-meta-box">
              <span class="sup-meta-label">{{ dashboardText('สถานที่', 'Location') }}</span>
              <strong class="sup-meta-val">{{ getTaskLocation(selectedTodo) }}</strong>
            </div>

            <div class="sup-meta-box">
              <span class="sup-meta-label">{{ dashboardText('ประเภทงาน', 'Category') }}</span>
              <strong class="sup-meta-val">{{ getTaskCategory(selectedTodo) }}</strong>
            </div>
          </div>

          <!-- Problem Block -->
          <div class="sup-problem-box mt-3">
            <span class="sup-problem-box-title">{{ dashboardText('รายละเอียดปัญหา', 'Problem Details') }}</span>
            <p class="sup-problem-box-desc">
              {{
                selectedTodo.userPart &&
                  selectedTodo.userPart.problemDetails
                  ? selectedTodo.userPart.problemDetails
                  : "-"
              }}
            </p>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="sup-detail-footer">
          <button type="button" class="btn btn-primary sup-assign-btn w-100 font-weight-bold d-flex align-items-center justify-content-center gap-2" @click="assignTechnician(selectedTodo)">
            <CIcon name="cil-user-plus" />
            {{ dashboardText('มอบหมายช่าง', 'Assign Technician') }}
          </button>
        </div>
      </div>

    </div>

    <!-- =====================================================
         TEAM WORKLOAD CARD (TECHNICIANS & SUPERVISOR)
    ====================================================== -->
    <div v-if="['workload', 'all'].includes(activeDashboardTab)" class="work-card workload-card mb-3">
      <div class="work-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4>{{ dashboardText('ภาระงานของทีม', 'Team Workload Overview') }}</h4>
          <p>{{ dashboardText('ภาพรวมภาระงานของช่างเทคนิคและซูเปอร์ไวเซอร์ในช่วงเวลาที่เลือก', 'Overview of technician and supervisor workload for the selected period') }}</p>
        </div>
        <div class="workload-tab-segmented">
          <button
            type="button"
            class="segmented-btn"
            :class="{ active: activeWorkloadTab === 'supervisor' }"
            @click="activeWorkloadTab = 'supervisor'"
          >
            <CIcon name="cil-user" class="mr-1" />
            {{ dashboardText('ซูเปอร์ไวเซอร์', 'Supervisor') }} ({{ supervisorWorkload ? 1 : 0 }})
          </button>
          <button
            type="button"
            class="segmented-btn"
            :class="{ active: activeWorkloadTab === 'technician' }"
            @click="activeWorkloadTab = 'technician'"
          >
            <CIcon name="cil-wrench" class="mr-1" />
            {{ dashboardText('ช่างเทคนิค', 'Technicians') }} ({{ technicianWorkload.length }})
          </button>
        </div>
      </div>

      <!-- TECHNICIAN TAB CONTENT -->
      <div v-if="activeWorkloadTab === 'technician'">
        <div class="technician-workload-wrapper d-none d-md-block">
          <table class="technician-workload-table">
            <thead>
              <tr>
                <th>{{ dashboardText('ช่าง', 'Technician') }}</th>
                <th>{{ dashboardText('สถานะ', 'Availability') }}</th>
                <th>{{ dashboardText('งานปัจจุบัน', 'Current Tickets') }}</th>
                <th>{{ dashboardText('ทั้งหมด', 'Total') }}</th>
                <th>{{ dashboardText('กำลังทำ', 'Active') }}</th>
                <th>{{ dashboardText('เสร็จแล้ว', 'Completed') }}</th>
                <th>{{ dashboardText('เกิน SLA', 'Over SLA') }}</th>
                <th>{{ dashboardText('ภาระงาน', 'Workload') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="technician in paginatedTechnicianWorkload" :key="technician._id" :id="'tech-' + technician._id">
                <td :data-label="dashboardText('ช่าง', 'Technician')"><strong>{{ technician.name }}</strong></td>
                <td :data-label="dashboardText('สถานะ', 'Availability')"><span class="technician-availability" :class="technician.availabilityClass">{{ dashboardText(technician.activeJobs > 0 ? 'ไม่ว่าง' : 'ว่าง', technician.availability) }}</span></td>
                <td :data-label="dashboardText('งานปัจจุบัน', 'Current Tickets')">
                  <div v-if="technician.currentTickets.length" class="technician-current-tickets">
                    <div v-for="item in technician.currentTickets" :key="item.id" class="current-ticket-line">
                      <span><b>{{ item.number }}</b> — {{ item.jobType }}</span>
                      <span class="current-ticket-status" :class="item.sla.className">{{ item.sla.text }}</span>
                    </div>
                    <small v-if="technician.remainingCurrentTickets">+{{ technician.remainingCurrentTickets }} {{ dashboardText('งาน', 'jobs') }}</small>
                  </div>
                  <span v-else class="no-current-ticket">{{ dashboardText('ไม่มีงานปัจจุบัน', 'No current tickets') }}</span>
                </td>
                <td :data-label="dashboardText('ทั้งหมด', 'Total')">{{ technician.totalJobs }}</td>
                <td :data-label="dashboardText('กำลังทำ', 'Active')">{{ technician.activeJobs }}</td>
                <td :data-label="dashboardText('เสร็จแล้ว', 'Completed')">{{ technician.completedJobs }}</td>
                <td :data-label="dashboardText('เกิน SLA', 'Over SLA')">{{ technician.overdueJobs }}</td>
                <td :data-label="dashboardText('ภาระงาน', 'Workload')">
                  <div class="workload-progress">
                    <span><i :style="{ width: (Number(technician.workloadPercent) || 0) + '%' }"></i></span>
                    <b>{{ Math.round(Number(technician.workloadPercent) || 0) }}%</b>
                  </div>
                </td>
              </tr>
              <tr v-if="!technicianWorkload.length">
                <td colspan="8" class="technician-workload-empty">{{ dashboardText('ไม่พบข้อมูลช่างเทคนิค', 'No technicians found') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Technician Workload Cards -->
        <div class="mobile-ticket-card-list d-md-none">
          <div v-for="technician in paginatedTechnicianWorkload" :key="'m-tech-' + technician._id" :id="'m-tech-' + technician._id" class="mobile-ticket-card">
            <div class="mobile-ticket-card-header">
              <span class="mobile-ticket-id">{{ technician.name }}</span>
              <span class="technician-availability" :class="technician.availabilityClass">
                {{ dashboardText(technician.activeJobs > 0 ? 'ไม่ว่าง' : 'ว่าง', technician.availability) }}
              </span>
            </div>
            <div class="mobile-ticket-card-body">
              <div class="mobile-card-row">
                <span class="mobile-row-label">{{ dashboardText('งานปัจจุบัน', 'Current Tickets') }}</span>
                <div v-if="technician.currentTickets.length" class="technician-current-tickets mt-1">
                  <div v-for="item in technician.currentTickets" :key="item.id" class="current-ticket-line">
                    <span><b>{{ item.number }}</b> — {{ item.jobType }}</span>
                    <span class="current-ticket-status" :class="item.sla.className">{{ item.sla.text }}</span>
                  </div>
                  <small v-if="technician.remainingCurrentTickets">+{{ technician.remainingCurrentTickets }} {{ dashboardText('งาน', 'jobs') }}</small>
                </div>
                <span v-else class="text-muted">{{ dashboardText('ไม่มีงานปัจจุบัน', 'No current tickets') }}</span>
              </div>
              <div class="d-flex justify-content-between flex-wrap gap-2">
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('ทั้งหมด', 'Total') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ technician.totalJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('กำลังทำ', 'Active') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ technician.activeJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('เสร็จแล้ว', 'Completed') }}</span>
                  <span class="mobile-row-val font-weight-bold text-success">{{ technician.completedJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('เกิน SLA', 'Over SLA') }}</span>
                  <span class="mobile-row-val font-weight-bold text-danger">{{ technician.overdueJobs }}</span>
                </div>
              </div>
              <div class="mobile-card-row mt-1">
                <span class="mobile-row-label">{{ dashboardText('ภาระงาน', 'Workload') }}</span>
                <div class="workload-progress mt-1">
                  <span><i :style="{ width: (Number(technician.workloadPercent) || 0) + '%' }"></i></span>
                  <b>{{ Math.round(Number(technician.workloadPercent) || 0) }}%</b>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!technicianWorkload.length" class="technician-workload-empty text-center py-3">
            {{ dashboardText('ไม่พบข้อมูลช่างเทคนิค', 'No technicians found') }}
          </div>
        </div>

        <div v-if="technicianTotalPages > 1" class="todo-pagination technician-pagination">
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === 1" @click="technicianCurrentPage = 1">«</button>
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === 1" @click="technicianCurrentPage--">‹</button>
          <button v-for="page in technicianPageNumbers" :key="page" type="button" class="todo-page-button" :class="{ active: technicianCurrentPage === page }" @click="technicianCurrentPage = page">{{ page }}</button>
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === technicianTotalPages" @click="technicianCurrentPage++">›</button>
          <button type="button" class="todo-page-button" :disabled="technicianCurrentPage === technicianTotalPages" @click="technicianCurrentPage = technicianTotalPages">»</button>
        </div>
      </div>

      <!-- SUPERVISOR TAB CONTENT -->
      <div v-if="activeWorkloadTab === 'supervisor'">
        <div class="technician-workload-wrapper d-none d-md-block">
          <table class="technician-workload-table supervisor-workload-table">
            <thead>
              <tr>
                <th>{{ dashboardText('Supervisor', 'Supervisor') }}</th>
                <th>{{ dashboardText('ฝ่าย/แผนก', 'Department / Section') }}</th>
                <th>{{ dashboardText('งานที่ได้รับมอบหมาย', 'Assigned Jobs') }}</th>
                <th>{{ dashboardText('ตอบรับแล้ว', 'Accepted') }}</th>
                <th>{{ dashboardText('กำลังดำเนินการ', 'In Progress') }}</th>
                <th>{{ dashboardText('เสร็จแล้ว', 'Completed') }}</th>
                <th>{{ dashboardText('ภาระงาน', 'Workload') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="supervisorWorkload" :id="'sup-' + (supervisorWorkload.id || 'sup')">
                <td :data-label="dashboardText('Supervisor', 'Supervisor')"><strong>{{ supervisorWorkload.name }}</strong></td>
                <td :data-label="dashboardText('ฝ่าย/แผนก', 'Department / Section')">
                  <span class="workload-role">{{ dashboardText('Supervisor', 'Supervisor') }}</span>
                  <small v-if="supervisorWorkload.section" class="workload-section ml-1">{{ supervisorWorkload.section }}</small>
                </td>
                <td :data-label="dashboardText('งานที่ได้รับมอบหมาย', 'Assigned Jobs')">{{ supervisorWorkload.assignedJobs }}</td>
                <td :data-label="dashboardText('ตอบรับแล้ว', 'Accepted')">{{ supervisorWorkload.acceptedJobs }}</td>
                <td :data-label="dashboardText('กำลังดำเนินการ', 'In Progress')">{{ supervisorWorkload.inProgressJobs }}</td>
                <td :data-label="dashboardText('เสร็จแล้ว', 'Completed')">{{ supervisorWorkload.completedJobs }}</td>
                <td :data-label="dashboardText('ภาระงาน', 'Workload')">
                  <div class="workload-progress">
                    <span><i :style="{ width: (Number(supervisorWorkload.workloadPercent) || 0) + '%' }"></i></span>
                    <b>{{ Math.round(Number(supervisorWorkload.workloadPercent) || 0) }}%</b>
                  </div>
                </td>
              </tr>
              <tr v-else>
                <td colspan="7" class="technician-workload-empty">{{ dashboardText('ไม่พบข้อมูลซูเปอร์ไวเซอร์', 'Supervisor data unavailable') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Supervisor Workload Cards -->
        <div class="mobile-ticket-card-list d-md-none">
          <div v-if="supervisorWorkload" :id="'m-sup-' + (supervisorWorkload.id || 'sup')" class="mobile-ticket-card">
            <div class="mobile-ticket-card-header">
              <span class="mobile-ticket-id">{{ supervisorWorkload.name }}</span>
              <span class="mobile-ticket-badge badge-role">{{ dashboardText('Supervisor', 'Supervisor') }}</span>
            </div>
            <div class="mobile-ticket-card-body">
              <div class="mobile-card-row" v-if="supervisorWorkload.section">
                <span class="mobile-row-label">{{ dashboardText('ฝ่าย/แผนก', 'Department / Section') }}</span>
                <span class="mobile-row-val">{{ supervisorWorkload.section }}</span>
              </div>
              <div class="d-flex justify-content-between flex-wrap gap-2">
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('งานที่ได้รับมอบหมาย', 'Assigned Jobs') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ supervisorWorkload.assignedJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('ตอบรับแล้ว', 'Accepted') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ supervisorWorkload.acceptedJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('กำลังดำเนินการ', 'In Progress') }}</span>
                  <span class="mobile-row-val font-weight-bold">{{ supervisorWorkload.inProgressJobs }}</span>
                </div>
                <div class="mobile-card-row">
                  <span class="mobile-row-label">{{ dashboardText('เสร็จแล้ว', 'Completed') }}</span>
                  <span class="mobile-row-val font-weight-bold text-success">{{ supervisorWorkload.completedJobs }}</span>
                </div>
              </div>
              <div class="mobile-card-row mt-1">
                <span class="mobile-row-label">{{ dashboardText('ภาระงาน', 'Workload') }}</span>
                <div class="workload-progress mt-1">
                  <span><i :style="{ width: (Number(supervisorWorkload.workloadPercent) || 0) + '%' }"></i></span>
                  <b>{{ Math.round(Number(supervisorWorkload.workloadPercent) || 0) }}%</b>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="technician-workload-empty text-center py-3">
            {{ dashboardText('ไม่พบข้อมูลซูเปอร์ไวเซอร์', 'Supervisor data unavailable') }}
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         ANALYTICS / HIGHLIGHT
    ====================================================== -->
    <div v-if="['analytics', 'all'].includes(activeDashboardTab)" class="analytics-layout mb-3">
      <div class="analytics-card main-chart-card">
        <div class="analytics-card-header">
          <div>
            <h4 class="m-0 font-weight-bold text-dark">
              <CIcon name="cil-chart-line" class="text-primary mr-2" />
              {{ dashboardText('Top 5 รายการแจ้งซ่อมสูงสุด', 'Top 5 Most Requested Repairs') }}
            </h4>
            <p class="m-0 text-muted small mt-1">
              {{ dashboardText('สถิติ 5 อันดับประเภทงานซ่อมที่ได้รับแจ้งมากที่สุดในช่วงเวลาที่เลือก', 'Top 5 most reported repair categories for the selected period') }}
            </p>
          </div>
        </div>
        <!-- Top 5 Most Requested Repairs Report -->
        <div class="top5-chart-wrapper">
          <div class="top5-items-list">
            <div
              v-for="item in top5RepairRequests"
              :key="item.rank"
              class="top5-item-row"
            >
              <!-- Rank badge -->
              <div class="top5-rank-col">
                <span class="top5-rank-badge" :class="'rank-' + item.rank">
                  #{{ item.rank }}
                </span>
              </div>

              <!-- Category Icon -->
              <div class="top5-icon-col">
                <div class="top5-icon-tile">
                  <CIcon :name="item.icon" size="lg" />
                </div>
              </div>

              <!-- Info & Progress Bar -->
              <div class="top5-info-col">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="top5-title-group">
                    <span class="top5-name font-weight-bold">{{ item.name }}</span>
                    <span v-if="item.isDefault" class="badge badge-light ml-2 text-muted" style="font-size: 0.68rem;">
                      {{ dashboardText('หมวดแนะนำ', 'Recommended') }}
                    </span>
                  </div>
                  <div class="top5-counts">
                    <span class="font-weight-bold text-primary">{{ item.count }}</span>
                    <small class="text-muted ml-1">{{ dashboardText('งาน', 'jobs') }}</small>
                    <small class="text-muted ml-2">({{ item.percentage }}%)</small>
                  </div>
                </div>

                <!-- Custom Progress Bar -->
                <div class="top5-bar-track">
                  <div
                    class="top5-bar-fill"
                    :class="'fill-rank-' + item.rank"
                    :style="{ width: item.barWidth + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="analytics-side">
        <div class="analytics-card performance-card">
          <div class="analytics-card-header compact">
            <div>
              <h4>{{ dashboardText('ประสิทธิภาพ', 'Performance') }}</h4>
              <p>{{ dashboardText('สัดส่วนงานที่ปิดแล้ว', 'Completed work ratio') }}</p>
            </div>
          </div>
          <div class="performance-content">
            <div class="donut-wrapper">
              <CChartDoughnut :datasets="donutDatasets" :labels="donutLabels" :options="donutOptions" />
              <div class="donut-center">
                <strong>{{ completionRate }}%</strong>
                <span>{{ dashboardText('เสร็จแล้ว', 'Complete') }}</span>
              </div>
            </div>
            <div class="donut-breakdown-row d-flex justify-content-around align-items-center mt-2 pt-2 border-top w-100">
              <div class="donut-breakdown-item text-center">
                <span class="donut-breakdown-label d-block text-muted small">{{ dashboardText('เสร็จสิ้น', 'Done') }}</span>
                <b class="donut-breakdown-val text-success">{{ stats.completed }}</b>
              </div>
              <div class="donut-breakdown-divider"></div>
              <div class="donut-breakdown-item text-center">
                <span class="donut-breakdown-label d-block text-muted small">{{ dashboardText('รอดำเนินการ', 'Active') }}</span>
                <b class="donut-breakdown-val text-primary">{{ Math.max(0, stats.total - stats.completed) }}</b>
              </div>
              <div class="donut-breakdown-divider"></div>
              <div class="donut-breakdown-item text-center">
                <span class="donut-breakdown-label d-block text-muted small">{{ dashboardText('ทั้งหมด', 'Total') }}</span>
                <b class="donut-breakdown-val text-dark">{{ stats.total }}</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =====================================================
         PARTS / EQUIPMENT OVERVIEW
    ====================================================== -->
    <div v-if="['parts', 'all'].includes(activeDashboardTab)" class="work-card parts-overview-card mb-3">
      <div class="work-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 class="m-0 font-weight-bold">{{ dashboardText('อะไหล่ / อุปกรณ์ที่ใช้', 'Parts / Equipment Used') }}</h4>
          <p class="m-0 text-muted small">{{ dashboardText('รายการและค่าใช้จ่ายจากงานในช่วงเวลาที่เลือก', 'Parts and costs for tickets in the selected period') }}</p>
        </div>
      </div>
      <div class="technician-workload-wrapper d-none d-md-block">
        <table class="technician-workload-table parts-workload-table">
          <thead>
            <tr>
              <th>{{ dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.') }}</th>
              <th>{{ dashboardText('ประเภทงาน', 'Job Type') }}</th>
              <th class="text-center">{{ dashboardText('รายการอะไหล่ / อุปกรณ์', 'Parts / Equipment') }}</th>
              <th class="text-center">{{ dashboardText('จำนวนรวม', 'Total Qty') }}</th>
              <th class="text-right">{{ dashboardText('รวมทั้งสิ้น (บาท)', 'Total (THB)') }}</th>
              <th class="text-center">{{ dashboardText('การจัดการ', 'Actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in paginatedPartsTickets" :key="ticket.id" :id="'parts-' + ticket.id">
              <td :data-label="dashboardText('เลขที่แจ้งซ่อม', 'Ticket No.')">
                <strong>{{ ticket.number }}</strong>
              </td>
              <td :data-label="dashboardText('ประเภทงาน', 'Job Type')">
                <span>{{ ticket.jobType }}</span>
              </td>
              <td :data-label="dashboardText('รายการอะไหล่ / อุปกรณ์', 'Parts / Equipment')" class="text-center">
                <div v-if="ticket.parts.length" class="parts-inline-list">
                  <span v-for="(part, index) in ticket.parts.slice(0, 3)" :key="ticket.id + '-' + index" class="parts-inline-tag">
                    {{ part.name }} (x{{ part.quantity }})
                  </span>
                  <small v-if="ticket.parts.length > 3" class="parts-more-count ml-1">+{{ ticket.parts.length - 3 }} {{ dashboardText('รายการ', 'items') }}</small>
                </div>
                <span v-else class="text-muted font-italic">{{ dashboardText('ไม่ใช้อะไหล่', 'No parts used') }}</span>
              </td>
              <td :data-label="dashboardText('จำนวนรวม', 'Total Qty')" class="text-center">
                <span class="parts-qty-badge">{{ ticket.parts.reduce((sum, p) => sum + p.quantity, 0) }}</span>
              </td>
              <td :data-label="dashboardText('รวมทั้งสิ้น (บาท)', 'Total (THB)')" class="text-right">
                <span class="parts-price-val" :class="{ 'has-cost': ticket.total > 0 }">{{ formatCurrency(ticket.total) }}</span>
              </td>
              <td :data-label="dashboardText('การจัดการ', 'Actions')" class="text-center">
                <button type="button" class="parts-detail-button" @click="openPartsDetail(ticket)">
                  {{ dashboardText('ดูรายละเอียด', 'View Details') }}
                </button>
              </td>
            </tr>
            <tr v-if="!paginatedPartsTickets.length">
              <td colspan="6" class="technician-workload-empty parts-empty">{{ dashboardText('ไม่พบข้อมูลอะไหล่ในช่วงเวลานี้', 'No parts data for this period') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Parts Cards (media max-width: 768px) -->
      <div class="mobile-ticket-card-list d-md-none">
        <div v-for="ticket in paginatedPartsTickets" :key="'m-part-' + ticket.id" class="mobile-ticket-card">
          <div class="mobile-ticket-card-header">
            <span class="mobile-ticket-id">{{ ticket.number }}</span>
            <span class="mobile-job-type-pill">{{ ticket.jobType }}</span>
          </div>
          <div class="mobile-ticket-card-body">
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('รายการอะไหล่ / อุปกรณ์', 'Parts / Equipment') }}</span>
              <div v-if="ticket.parts.length" class="parts-inline-list mt-1">
                <span v-for="(part, index) in ticket.parts.slice(0, 3)" :key="ticket.id + '-' + index" class="parts-inline-tag">
                  {{ part.name }} (x{{ part.quantity }})
                </span>
                <small v-if="ticket.parts.length > 3" class="parts-more-count ml-1">+{{ ticket.parts.length - 3 }} {{ dashboardText('รายการ', 'items') }}</small>
              </div>
              <span v-else class="text-muted font-italic">{{ dashboardText('ไม่ใช้อะไหล่', 'No parts used') }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-2">
              <div class="mobile-card-row mb-0">
                <span class="mobile-row-label">{{ dashboardText('จำนวนรวม', 'Total Qty') }}</span>
                <span class="mobile-row-val font-weight-bold">{{ ticket.parts.reduce((sum, p) => sum + p.quantity, 0) }}</span>
              </div>
              <div class="mobile-card-row mb-0">
                <span class="mobile-row-label">{{ dashboardText('รวมทั้งสิ้น', 'Total') }}</span>
                <span class="mobile-row-val font-weight-bold text-dark">{{ formatCurrency(ticket.total) }} {{ dashboardText('บาท', 'THB') }}</span>
              </div>
            </div>
          </div>
          <button type="button" class="mobile-view-details-btn" @click="openPartsDetail(ticket)">
            {{ dashboardText('ดูรายละเอียด', 'View Details') }}
          </button>
        </div>
        <div v-if="!paginatedPartsTickets.length" class="technician-workload-empty text-center py-3">
          {{ dashboardText('ไม่พบข้อมูลอะไหล่ในช่วงเวลานี้', 'No parts data for this period') }}
        </div>
      </div>

      <!-- Parts Table Footer Strip -->
      <div class="parts-table-footer d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div v-if="partsTotalPages > 1" class="todo-pagination m-0">
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === 1" @click="partsCurrentPage = 1">«</button>
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === 1" @click="partsCurrentPage--">‹</button>
          <button v-for="page in partsPageNumbers" :key="page" type="button" class="todo-page-button" :class="{ active: partsCurrentPage === page }" @click="partsCurrentPage = page">{{ page }}</button>
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === partsTotalPages" @click="partsCurrentPage++">›</button>
          <button type="button" class="todo-page-button" :disabled="partsCurrentPage === partsTotalPages" @click="partsCurrentPage = partsTotalPages">»</button>
        </div>
        <div v-else></div>

        <div class="parts-summary-strip d-flex align-items-center gap-3">
          <span class="parts-summary-chip">
            <span class="chip-label mr-1">{{ dashboardText('งานที่ใช้อะไหล่:', 'Tickets with parts:') }}</span>
            <b class="chip-value">{{ partsSummary.ticketsWithParts }}</b>
          </span>
          <span class="parts-summary-chip">
            <span class="chip-label mr-1">{{ dashboardText('รายการอะไหล่รวม:', 'Total items:') }}</span>
            <b class="chip-value">{{ partsSummary.itemCount }}</b>
          </span>
          <span class="parts-summary-chip parts-summary-total">
            <span class="chip-label mr-1">{{ dashboardText('ยอดรวมทั้งสิ้น:', 'Grand Total:') }}</span>
            <b class="chip-value-total">{{ formatCurrency(partsSummary.totalCost) }}</b>
            <span class="chip-unit ml-1">{{ dashboardText('บาท', 'THB') }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- =====================================================
         TICKET TABLE
    ====================================================== -->
    <div v-if="['parts', 'all'].includes(activeDashboardTab)" class="work-card report-table-card">
      <div class="work-card-header d-flex justify-content-between align-items-center flex-wrap">
        <h4 class="mb-0 py-1">
          <CIcon name="cil-list" class="mr-2"></CIcon> {{ dashboardText('รายละเอียดใบงาน', 'Ticket Details') }}
        </h4>
      </div>

      <div class="report-table-wrapper d-none d-md-block">
        <CDataTable
          :items="formattedTickets"
          :fields="reportFields"
          :column-filter="true"
          :table-filter="tableFilterConfig"
          :items-per-page-select="itemsPerPageSelectConfig"
          :items-per-page="tableItemsPerPage"
          :active-page.sync="tableCurrentPage"
          @pagination-change="tableItemsPerPage = $event"
          sorter
          cleaner
          hover
          pagination
        >
          <template #ticketNo="{ item }">
            <td :id="'ticket-row-' + item.ticketNo" class="font-weight-bold">
              <a href="javascript:void(0)" class="dashboard-ticket-link" @click="goToTicketDetail(item)" :title="dashboardText('เปิดดูในหน้า Home', 'View in Home')">
                {{ item.ticketNo }}
                <CIcon name="cil-external-link" class="ticket-link-icon" />
              </a>
            </td>
          </template>

          <template #reporterDepartment="{ item }">
            <td>{{ item.reporterDepartment }}</td>
          </template>

          <template #reporter="{ item }">
            <td>{{ item.reporter }}</td>
          </template>

          <template #status="{ item }">
            <td>
              <CBadge :color="getBadge(item._status_raw)">{{ item.status }}</CBadge>
            </td>
          </template>

          <template #sla="{ item }">
            <td>
              <CBadge :color="item._slaOverdue ? 'danger' : 'success'">{{ item.sla }}</CBadge>
              <small v-if="item._sla_hours" class="d-block text-muted">
                {{ $t("dashboard.slaTarget") }} {{ item._sla_hours }}h
              </small>
            </td>
          </template>
        </CDataTable>
      </div>

      <!-- Mobile Ticket Cards (media max-width: 768px) -->
      <div class="mobile-ticket-card-list d-md-none">
        <div
          v-for="(item, idx) in formattedTickets"
          :key="'m-rep-' + item.ticketNo + '-' + idx"
          :id="'m-rep-' + item.ticketNo + '-' + idx"
          class="mobile-ticket-card"
          @click="goToTicketDetail(item)"
        >
          <div class="mobile-ticket-card-header">
            <span class="mobile-ticket-id">{{ item.ticketNo }}</span>
            <span class="mobile-ticket-badge" :class="'badge-' + getBadge(item._status_raw)">
              {{ item.status }}
            </span>
          </div>
          <div class="mobile-ticket-card-body">
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('ผู้แจ้ง', 'Requester') }}</span>
              <span class="mobile-row-val">{{ item.reporter }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('แผนก', 'Department') }}</span>
              <span class="mobile-row-val">{{ item.reporterDepartment }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('ประเภทงาน', 'Category') }}</span>
              <span class="mobile-row-val">{{ item.category }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('วันที่แจ้ง', 'Date Informed') }}</span>
              <span class="mobile-row-val">{{ item.dateInformed }}</span>
            </div>
            <div class="mobile-card-row">
              <span class="mobile-row-label">{{ dashboardText('SLA', 'SLA') }}</span>
              <span class="mobile-row-val">
                <CBadge :color="item._slaOverdue ? 'danger' : 'success'">{{ item.sla }}</CBadge>
                <small v-if="item._sla_hours" class="ml-1 text-muted">
                  ({{ $t("dashboard.slaTarget") }} {{ item._sla_hours }}h)
                </small>
              </span>
            </div>
          </div>
          <button type="button" class="mobile-view-details-btn" @click.stop="goToTicketDetail(item)">
            {{ dashboardText('ดูรายละเอียด', 'View Details') }}
          </button>
        </div>
        <div v-if="!formattedTickets.length" class="text-center py-4 text-muted">
          {{ dashboardText('ไม่มีข้อมูล', 'No data') }}
        </div>
      </div>
    </div>
    </div>
    <TicketDetailModal v-if="selectedReport" @close="selectedReport=null">
      <ReportDetail :report="selectedReport" @close="selectedReport=null" @refresh="fetchData" />
    </TicketDetailModal>
  </div>
</template>
<script>
import keyboardActivate from '@/directives/keyboardActivate';
import listboxEscape from '@/directives/listboxEscape';
import keyboardSearch from '@/mixins/keyboardSearch';
import CModal from '@/components/common/AccessibleModal.vue';
import moment from "moment";
import Swal from "sweetalert2";
import Service from "@/service/api";
import TicketDetailModal from "@/components/common/TicketDetailModal.vue";
import ReportDetail from "./ReportDetail.vue";
import { matchTicketBilingual } from "@/utils/buildingBilingual.js";
import { getTicketLocationFull } from "@/utils/ticketViewHelper.js";

import {
  CChartBar,
  CChartDoughnut,
} from "@coreui/vue-chartjs";

import { mapGetters } from "vuex";
import { getSocket } from "@/service/socketio.service";
import { getTicketReporterAffiliation } from "@/utils/ticketViewHelper";

export default {
  directives: { keyboardActivate, listboxEscape },
  mixins: [keyboardSearch],
  name: "SupervisorDashboard",

  components: { CModal,
    CChartBar,
    CChartDoughnut,
    TicketDetailModal,
    ReportDetail,
  },

  data() {
    return {
      selectedReport: null,
      /* =====================================================
         RANGE
      ====================================================== */

      selectedRange: "week",

      startDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
      endDate: moment().endOf('isoWeek').format('YYYY-MM-DD'),
      activeDashboardTab: "todo",
      activeWorkloadTab: "technician",
      searchQuery: "",
      selectedHotKey: "",
      currentMatchIndex: 0,
      showSearchDropdown: false,
      hasNavigatedMatch: false,

      /* =====================================================
         DATA
      ====================================================== */

      tickets: [],
      categories: [],
      technicians: [],
      currentRepairWorkloads: [],
      workloadLoading: false,
      workloadError: null,

      stats: {
        total: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        satisfaction: 0,
        awaitingEvaluation: 0,
      },

      /* =====================================================
         TODO
      ====================================================== */

      selectedTodo: null,

      /* =====================================================
         PAGINATION
      ====================================================== */
      todoCurrentPage: 1,
      todoItemsPerPage: 5,
      mobileTodoFilter: 'in_progress',
      mobileTodoCurrentPage: 1,
      mobileTodoItemsPerPage: 5,
      technicianCurrentPage: 1,
      technicianItemsPerPage: 5,
      partsCurrentPage: 1,
      partsItemsPerPage: 5,
      showPartsDetailModal: false,
      selectedPartsTicket: null,
      tableItemsPerPage: 5,
      tableCurrentPage: 1,

      currentTime: Date.now(),
      slaCountdownTimer: null,

      /* =====================================================
         SOCKET
      ====================================================== */
      socket: null,
    };
  },

  computed: {
    tableFilterConfig() {
      return {
        label: this.$t('table.filterLabel'),
        placeholder: this.$t('table.filterPlaceholder')
      };
    },
    itemsPerPageSelectConfig() {
      return {
        label: this.$t('table.itemsPerPageLabel'),
        values: [10, 20, 50]
      };
    },

    /* =====================================================
       TODO PAGINATION
    ====================================================== */

    todoTotalPages() {
      return Math.max(
        1,
        Math.ceil(
          this.todoList.length / this.todoItemsPerPage
        )
      );
    },

    paginatedTodoTickets() {
      const start =
        (this.todoCurrentPage - 1) * this.todoItemsPerPage;

      const end = start + this.todoItemsPerPage;

      return this.todoList.slice(start, end);
    },

    todoPageNumbers() {
      const total = this.todoTotalPages;
      const current = this.todoCurrentPage;

      if (total <= 5) {
        return Array.from(
          { length: total },
          (_, index) => index + 1
        );
      }

      let start = Math.max(1, current - 2);
      let end = Math.min(total, start + 4);

      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }

      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },

    filteredMobileTodoTickets() {
      if (this.mobileTodoFilter === 'in_progress') {
        return this.todoList.filter(t => t.status === 'in_progress' || t.status === 'assigned');
      }
      if (this.mobileTodoFilter === 'pending') {
        return this.todoList.filter(t => t.status === 'pending');
      }
      return this.todoList;
    },

    inProgressTodoCount() {
      return this.todoList.filter(t => t.status === 'in_progress' || t.status === 'assigned').length;
    },

    pendingTodoCount() {
      return this.todoList.filter(t => t.status === 'pending').length;
    },

    mobileTodoTotalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredMobileTodoTickets.length / this.mobileTodoItemsPerPage)
      );
    },

    paginatedMobileTodoTickets() {
      const start = (this.mobileTodoCurrentPage - 1) * this.mobileTodoItemsPerPage;
      const end = start + this.mobileTodoItemsPerPage;
      return this.filteredMobileTodoTickets.slice(start, end);
    },

    mobileTodoPageNumbers() {
      const total = this.mobileTodoTotalPages;
      const current = this.mobileTodoCurrentPage;

      if (total <= 5) {
        return Array.from({ length: total }, (_, index) => index + 1);
      }

      let start = Math.max(1, current - 2);
      let end = Math.min(total, start + 4);

      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }

      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },

    technicianWorkload() {
      return this.technicians
        .filter((technician) => technician && technician.role === "technician")
        .map((technician) => {
          const personId = String(technician.personId || technician._id || technician.id || "");
          const assignedTickets = (this.filteredTickets || []).filter((ticket) => {
            const tech = ticket?.supervisorPart?.assignedTechnician || ticket?.technicianPart?.technician || ticket?.assignedTechnician || ticket?.technician;
            const techId = typeof tech === "object" ? tech?._id || tech?.id || tech?.personId : tech;
            if (techId && String(techId) === personId) return true;
            if (Array.isArray(ticket?.assignees)) {
              return ticket.assignees.some((a) => {
                const aId = typeof a === "object" ? a?._id || a?.id || a?.personId : a;
                return String(aId) === personId;
              });
            }
            return false;
          });

          const totalJobs = assignedTickets.length;
          const activeJobs = assignedTickets.filter((ticket) =>
            ["pending", "assigned", "in_progress"].includes(ticket.status)
          ).length;
          const completedJobs = assignedTickets.filter((ticket) =>
            ["done", "welldone"].includes(ticket.status)
          ).length;
          const overdueJobs = assignedTickets.filter((ticket) =>
            this.isTicketOverdue(ticket)
          ).length;

          const completionRate =
            totalJobs > 0
              ? Math.min(100, Math.round((completedJobs / totalJobs) * 100))
              : 0;

          const currentWorkload = this.currentRepairWorkloads?.find((worker) =>
            String(worker?.personId || worker?._id || worker?.id || "") === personId
          );

          const workloadPercent = Number(currentWorkload?.workloadPercent) || (totalJobs > 0 ? Math.min(100, Math.round((activeJobs / totalJobs) * 100)) : 0);

          const currentTickets = assignedTickets
            .filter((ticket) => ["pending", "assigned", "in_progress"].includes(ticket.status))
            .map((ticket) => ({
              id: ticket._id || ticket.ticketNumber,
              number: ticket.ticketNumber || ticket._id || "-",
              jobType:
                ticket.userPart?.subCategory?.name ||
                ticket.userPart?.subCategory ||
                "-",
              status: ticket.status,
              overdue: this.isTicketOverdue(ticket),
              sla: this.getSlaCountdown(ticket),
            }));

          const remainingCurrentTickets = Math.max(0, currentTickets.length - 3);

          return {
            _id: technician._id || personId,
            personId,
            name: `${technician.name || ""} ${technician.surname || ""}`.trim() || technician.email || "-",
            availability: activeJobs > 0 ? "Busy" : "Available",
            availabilityClass: activeJobs > 0 ? "is-busy" : "is-available",
            currentTickets: currentTickets.slice(0, 3),
            remainingCurrentTickets,
            totalJobs,
            activeJobs,
            completedJobs,
            overdueJobs,
            completionRate,
            workloadPercent: Math.round(Number(workloadPercent) || 0),
          };
        });
    },

    supervisorWorkload() {
      const supervisor = this.currentUser || this.getStoredUser() || {};
      const supervisorId = String(supervisor._id || supervisor.id || "");
      if (!supervisorId && !supervisor.name && !supervisor.email) return null;

      let assignedTickets = (this.filteredTickets || []).filter((ticket) => {
        const sup = ticket.supervisorPart?.supervisor || ticket.supervisor;
        const id = String(this.extractId(sup) || "");
        if (id && supervisorId && id === supervisorId) return true;

        if (Array.isArray(ticket.supervisorPart?.assignments)) {
          if (ticket.supervisorPart.assignments.some((a) => String(this.extractId(a?.assignee || a)) === supervisorId)) {
            return true;
          }
        }
        if (Array.isArray(ticket.assignees)) {
          if (ticket.assignees.some((a) => String(this.extractId(a)) === supervisorId)) {
            return true;
          }
        }
        return false;
      });

      if (!assignedTickets.length && (this.filteredTickets || []).length) {
        assignedTickets = this.filteredTickets.filter((ticket) => {
          const sup = ticket.supervisorPart?.supervisor || ticket.supervisor;
          const id = String(this.extractId(sup) || "");
          return !id || (supervisorId && id === supervisorId);
        });
        if (!assignedTickets.length) {
          assignedTickets = this.filteredTickets;
        }
      }

      const assignedJobs = assignedTickets.length;
      const acceptedJobs = assignedTickets.filter((ticket) => {
        const supStatus = ticket.supervisorPart?.status;
        const assignStatus = ticket.assignmentStatus;
        return (
          supStatus === "accepted" ||
          assignStatus === "accepted" ||
          ["assigned", "in_progress", "done", "welldone"].includes(ticket.status)
        );
      }).length;
      const inProgressJobs = assignedTickets.filter((ticket) =>
        ["pending", "assigned", "in_progress"].includes(ticket.status)
      ).length;
      const completedJobs = assignedTickets.filter((ticket) =>
        ["done", "welldone"].includes(ticket.status)
      ).length;

      let section = this.supervisorSectionName;
      if (!section) {
        const s = supervisor.section || supervisor.department;
        if (s && typeof s === "object") {
          section = s.departmentName || s.name || s.code || "";
        } else if (typeof s === "string") {
          section = s;
        }
      }
      if (!section && this.categories?.length) {
        const secId = String(this.extractId(supervisor.section || supervisor.department) || "");
        const matched = this.categories.find((c) => String(this.extractId(c)) === secId);
        if (matched) {
          section = matched.name || matched.departmentName || "";
        }
      }

      const currentWorkload = this.currentRepairWorkloads?.find((worker) =>
        supervisorId && String(worker?.personId || worker?._id || worker?.id || "") === supervisorId
      );

      const activeJobs = inProgressJobs;
      const workloadPercent =
        Number(currentWorkload?.workloadPercent) ||
        (assignedJobs > 0 ? Math.min(100, Math.round((activeJobs / assignedJobs) * 100)) : 0);

      const displayName =
        `${supervisor.name || ""} ${supervisor.surname || ""}`.trim() ||
        supervisor.username ||
        supervisor.email ||
        "Supervisor";

      return {
        id: supervisorId || "sup",
        name: displayName,
        role: supervisor.role || "supervisor",
        section: section || "",
        assignedJobs,
        acceptedJobs,
        inProgressJobs: assignedTickets.filter((ticket) => ticket.status === "in_progress").length,
        completedJobs,
        completionRate: assignedJobs > 0 ? Math.min(100, Math.round((completedJobs / assignedJobs) * 100)) : 0,
        workloadPercent: Math.round(Number(workloadPercent) || 0),
      };
    },

    sortedCurrentRepairWorkloads() {
      return [...this.currentRepairWorkloads]
      .filter((worker) => worker && typeof worker === "object" && worker.role === "supervisor")
        .sort((a, b) => (Number(b.workloadPercent) || 0) - (Number(a.workloadPercent) || 0));
    },

    technicianTotalPages() {
      return Math.max(1, Math.ceil(this.technicianWorkload.length / this.technicianItemsPerPage));
    },

    paginatedTechnicianWorkload() {
      const start = (this.technicianCurrentPage - 1) * this.technicianItemsPerPage;
      return this.technicianWorkload.slice(start, start + this.technicianItemsPerPage);
    },

    technicianPageNumbers() {
      const total = this.technicianTotalPages;
      if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);

      let start = Math.max(1, this.technicianCurrentPage - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    },

    partsTickets() {
      return this.filteredTickets
        .map((ticket) => {
          const parts = this.getTicketParts(ticket);
          const noPartsUsed = ticket?.technicianPart?.noPartsUsed === true;

          if (!parts.length && !noPartsUsed) return null;

          return {
            id: ticket._id || ticket.ticketNumber,
            number: ticket.ticketNumber || ticket._id || "-",
            jobType: this.formatPartsJobType(ticket?.userPart?.subCategory?.name || ticket?.userPart?.subCategory || "-"),
            parts,
            noPartsUsed,
            total: parts.reduce((sum, part) => sum + part.total, 0),
          };
        })
        .filter(Boolean);
    },

    partsSummary() {
      return this.partsTickets.reduce((summary, ticket) => {
        summary.totalCost += ticket.total;
        summary.itemCount += ticket.parts.length;
        if (ticket.parts.length) summary.ticketsWithParts += 1;
        return summary;
      }, { totalCost: 0, ticketsWithParts: 0, itemCount: 0 });
    },

    partsTotalPages() {
      return Math.max(1, Math.ceil(this.partsTickets.length / this.partsItemsPerPage));
    },

    paginatedPartsTickets() {
      const start = (this.partsCurrentPage - 1) * this.partsItemsPerPage;
      return this.partsTickets.slice(start, start + this.partsItemsPerPage);
    },

    partsPageNumbers() {
      const total = this.partsTotalPages;
      if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);
      let start = Math.max(1, this.partsCurrentPage - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    },

    slaWithinCount() {
      return Math.max(0, Number(this.stats.total || 0) - Number(this.stats.overdue || 0));
    },

    technicianAvailabilitySummary() {
      return this.technicianWorkload.reduce((summary, technician) => {
        summary.total += 1;
        summary[technician.activeJobs > 0 ? "busy" : "available"] += 1;
        return summary;
      }, { total: 0, available: 0, busy: 0 });
    },

    /* =====================================================
       CURRENT USER
    ====================================================== */

    currentUser() {
      return this.getStoredUser();
    },

    /* =====================================================
       RANGE OPTIONS
    ====================================================== */

    rangeOptions() {
      // แสดง Today / Week / Month / Year และ Filter (custom)
      return [
        {
          label: this.$t("dashboard.rangeWeek"),
          value: "week",
        },
        {
          label: this.$t("dashboard.rangeMonth"),
          value: "month",
        },
        {
          label: this.$t("dashboard.rangeYear"),
          value: "year",
        },
        {
          label: "",
          value: "custom",
        },
      ];
    },
    /* =====================================================
       FILTERED TICKETS
    ====================================================== */

    filteredTickets() {
      const now = moment();

      return this.tickets.filter((ticket) => {
        if (!ticket) {
          return false;
        }

        /*
         * Dashboard ไม่เอางาน cancelled
         * แต่ยังคงข้อมูลต้นฉบับใน this.tickets
         */
        if (ticket.status === "cancelled") {
          return false;
        }

        const date = this.getTicketDate(ticket);

        if (!date.isValid()) {
          return false;
        }

        switch (this.selectedRange) {
          case "today":
            return date.isSame(now, "day");

          case "week":
            return date.isSame(now, "isoWeek");

          case "month":
            return date.year() === now.year();

          case "year": {
            const startYear = now.year() - 4;
            return date.year() >= startYear && date.year() <= now.year();
          }

          case "custom": {
            if (!this.startDate || !this.endDate) {
              return true;
            }

            const start = moment(this.startDate).startOf("day");
            const end = moment(this.endDate).endOf("day");

            return date.isBetween(
              start,
              end,
              undefined,
              "[]"
            );
          }

          case "all":
          default:
            return true;
        }
      });
    },

    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return [];
      const q = this.searchQuery.trim();
      const allTickets = Array.isArray(this.tickets) ? this.tickets : [];
      const matches = allTickets.filter((t) => matchTicketBilingual(t, q));

      return matches.slice(0, 30).map((t, idx) => {
        const ticketNo = t.ticketNumber || (t._id || '').substring(0, 8).toUpperCase() || '-';
        const loc = getTicketLocationFull(t, this.$t, this.$store.state.locale.lang) || '-';
        const rawStatus = (t.status || '').toLowerCase();
        const prob = t.userPart?.problemDetails || '-';
        const reporter = this.getReporterName ? this.getReporterName(t) : (t.userPart?.fullname || t.userPart?.username || '-');
        const badgeColor = this.getBadge ? this.getBadge(rawStatus) : 'ticket';

        return {
          id: t._id || t.ticketNumber || idx,
          ticket: t,
          title: `${ticketNo} · ${loc}`,
          description: prob,
          meta: reporter ? `${this.dashboardText('ผู้แจ้ง', 'Reporter')}: ${reporter}` : '',
          sectionLabel: this.statusLabel ? this.statusLabel(rawStatus) : rawStatus,
          badgeClass: 'badge-' + badgeColor,
        };
      });
    },

    /* =====================================================
       TODO LIST
    ====================================================== */

    todoList() {
      return this.filteredTickets
        .filter((ticket) =>
          [
            "pending",
            "assigned",
            "in_progress",
          ].includes(ticket.status)
        )
        .slice()
        .sort((a, b) => {
          return (
            this.getTicketDate(a).valueOf() -
            this.getTicketDate(b).valueOf()
          );
        });
    },

    /* =====================================================
       COMPLETION RATE
    ====================================================== */

    completionRate() {
      const total = Number(this.stats.total) || 0;

      const completed =
        Number(this.stats.completed) || 0;

      if (!total) {
        return 0;
      }

      return Math.round(
        (completed / total) * 100
      );
    },

    /* =====================================================
       HIGHLIGHT CHART
    ====================================================== */

    highlightLabels() {
      const now = moment();

      switch (this.selectedRange) {
        case "today":
          return [
            "00:00",
            "03:00",
            "06:00",
            "09:00",
            "12:00",
            "15:00",
            "18:00",
            "21:00",
          ];

        case "week":
          return this.dashboardText(
            ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."],
            ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
          );

        case "month":
          return this.dashboardText(
            ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."],
            ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
          );

        case "year":
        case "all":
        default: {
          const currentYear = now.year();
          const adYears = Array.from({ length: 5 }, (_, i) => String(currentYear - 4 + i));
          const beYears = Array.from({ length: 5 }, (_, i) => String(currentYear - 4 + i + 543));
          return this.dashboardText(beYears, adYears);
        }

        case "custom":
          return this.customChartLabels;
      }
    },

    allYearLabels() {
      if (!this.filteredTickets.length) {
        return [
          String(moment().year()),
        ];
      }

      const years =
        this.filteredTickets
          .map((ticket) =>
            this.getTicketDate(ticket).year()
          )
          .filter((year) =>
            Number.isFinite(year)
          );

      if (!years.length) {
        return [
          String(moment().year()),
        ];
      }

      const minYear =
        Math.min(...years);

      const maxYear =
        Math.max(...years);

      return Array.from(
        {
          length:
            maxYear - minYear + 1,
        },
        (_, index) =>
          String(minYear + index)
      );
    },

    customChartLabels() {
      if (
        !this.startDate ||
        !this.endDate
      ) {
        return [];
      }

      const start =
        moment(this.startDate).startOf("day");

      const end =
        moment(this.endDate).startOf("day");

      if (
        !start.isValid() ||
        !end.isValid() ||
        end.isBefore(start)
      ) {
        return [];
      }

      const dayDifference =
        end.diff(start, "days");

      /*
       * ถ้าช่วงไม่เกิน 31 วัน
       * แสดงรายวัน
       */
      if (dayDifference <= 31) {
        const labels = [];

        const cursor =
          start.clone();

        while (
          cursor.isSameOrBefore(
            end,
            "day"
          )
        ) {
          labels.push(
            cursor.format("DD/MM")
          );

          cursor.add(1, "day");
        }

        return labels;
      }

      /*
       * ถ้ามากกว่า 31 วัน
       * แสดงรายเดือน
       */
      const labels = [];

      const cursor =
        start.clone().startOf("month");

      const last =
        end.clone().startOf("month");

      while (
        cursor.isSameOrBefore(
          last,
          "month"
        )
      ) {
        labels.push(
          cursor.format("MM/YYYY")
        );

        cursor.add(1, "month");
      }

      return labels;
    },

    /* =====================================================
       REQUEST CHART VALUES
    ====================================================== */

    requestChartValues() {
      return this.buildChartValues(
        this.filteredTickets
      );
    },

    /* =====================================================
       COMPLETED CHART VALUES
    ====================================================== */

    completedChartValues() {
      const completedTickets =
        this.filteredTickets.filter(
          (ticket) =>
            [
              "done",
              "welldone",
            ].includes(ticket.status)
        );

      return this.buildChartValues(
        completedTickets
      );
    },

    /* =====================================================
       HIGHLIGHT DATASETS
    ====================================================== */

    highlightDatasets() {
      return [
        {
          label: this.dashboardText("คำร้อง", "Requests"),
          data: this.requestChartValues,

          backgroundColor: "#1d4ed8",
          borderColor: "#1d4ed8",
          borderWidth: 0,

          barThickness: 18,
          maxBarThickness: 22,
        },
        {
          label: this.dashboardText("เสร็จแล้ว", "Completed"),
          data: this.completedChartValues,

          backgroundColor: "#43b883",
          borderColor: "#43b883",
          borderWidth: 0,

          barThickness: 18,
          maxBarThickness: 22,
        },
      ];
    },

    top5RepairRequests() {
      const allTickets = Array.isArray(this.filteredTickets) && this.filteredTickets.length > 0
        ? this.filteredTickets
        : (Array.isArray(this.tickets) ? this.tickets : []);

      const counts = {};
      const completedCounts = {};

      for (const t of allTickets) {
        if (!t) continue;
        const rawName = t.userPart?.subCategory?.name ||
          t.userPart?.subCategory ||
          t.userPart?.jobType ||
          t.categoryName ||
          t.jobType;
        if (!rawName || rawName === '-' || rawName === 'undefined') continue;
        const cleanName = String(rawName).trim();

        counts[cleanName] = (counts[cleanName] || 0) + 1;
        const st = String(t.status || '').toLowerCase();
        if (['completed', 'done', 'closed', 'finish', 'finished'].includes(st)) {
          completedCounts[cleanName] = (completedCounts[cleanName] || 0) + 1;
        }
      }

      // Sort by count descending
      const sorted = Object.keys(counts)
        .map(name => ({
          name,
          count: counts[name],
          completed: completedCounts[name] || 0,
          icon: this.getJobIcon(name),
          isDefault: false
        }))
        .sort((a, b) => b.count - a.count);

      // Default baseline if fewer than 5 items exist
      const defaultCategories = [
        { name: 'เครื่องปรับอากาศและระบบเครื่องกล', icon: 'cil-snowflake' },
        { name: 'ไฟฟ้าและระบบจ่ายไฟ', icon: 'cil-bolt' },
        { name: 'ระบบแสงสว่างและโคมไฟ', icon: 'cil-lightbulb' },
        { name: 'ประปาและสุขาภิบาล', icon: 'cil-drop' },
        { name: 'อินเทอร์เน็ตและเครือข่าย', icon: 'cil-wifi-signal-1' }
      ];

      const topItems = sorted.slice(0, 5);
      for (const def of defaultCategories) {
        if (topItems.length >= 5) break;
        if (!topItems.some(i => i.name === def.name)) {
          topItems.push({
            name: def.name,
            count: 0,
            completed: 0,
            icon: def.icon,
            isDefault: true
          });
        }
      }

      const maxCount = Math.max(1, ...topItems.map(i => i.count));
      const totalCount = topItems.reduce((sum, i) => sum + i.count, 0) || 1;

      const result = topItems.map((item, idx) => ({
        ...item,
        rank: idx + 1,
        percentage: item.count > 0 ? Math.round((item.count / totalCount) * 100) : 0,
        barWidth: item.count > 0 ? Math.max(12, Math.round((item.count / maxCount) * 100)) : 4
      }));

      // Auto sync top categories to localStorage for requester form hot keys
      try {
        const topNames = result.filter(i => i.count > 0).map(i => i.name);
        if (topNames.length > 0) {
          localStorage.setItem('smartfix_top_hotkeys', JSON.stringify(topNames));
        }
      } catch (e) {
        // ignore
      }

      return result;
    },

    highlightTrendItems() {
      const labels = this.highlightLabels || [];
      const totalList = this.requestChartValues || [];
      const doneList = this.completedChartValues || [];
      const maxVal = Math.max(1, ...totalList, ...doneList);
      return labels.map((label, idx) => {
        const total = totalList[idx] || 0;
        const done = doneList[idx] || 0;
        return {
          label,
          total,
          done,
          totalHeight: Math.max(3, Math.round((total / maxVal) * 150)),
          doneHeight: Math.max(3, Math.round((done / maxVal) * 150)),
        };
      });
    },

    highlightOptions() {
      return {
        responsive: true,

        maintainAspectRatio: false,

        legend: {
          display: true,

          position: "top",

          align: "end",

          labels: {
            boxWidth: 10,
            fontSize: 11,
            padding: 18,
            usePointStyle: true,
          },
        },

        tooltips: {
          mode: "index",
          intersect: false,
        },

        hover: {
          mode: "nearest",
          intersect: true,
        },

        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                precision: 0,

                fontColor:
                  "#8b8e98",

                padding: 10,
              },

              gridLines: {
                color:
                  "rgba(0,0,0,0.045)",

                drawBorder: false,
              },
            },
          ],

          xAxes: [
            {
              ticks: {
                fontColor:
                  "#8b8e98",

                maxRotation: 0,
                minRotation: 0,
              },

              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
        },
      };
    },

    /* =====================================================
       MINI BAR
    ====================================================== */

    miniBarValues() {
      const values =
        this.requestChartValues;

      if (!values.length) {
        return [
          0,
          0,
          0,
          0,
          0,
        ];
      }

      if (values.length <= 5) {
        return values;
      }

      /*
       * ย่อข้อมูลเหลือประมาณ 5 จุด
       * สำหรับ mini chart
       */
      const result = [];

      const chunkSize =
        Math.ceil(
          values.length / 5
        );

      for (
        let index = 0;
        index < values.length;
        index += chunkSize
      ) {
        const chunk =
          values.slice(
            index,
            index + chunkSize
          );

        result.push(
          chunk.reduce(
            (sum, value) =>
              sum + value,
            0
          )
        );
      }

      return result.slice(0, 5);
    },

    /* =====================================================
       MINI LINE
    ====================================================== */

    miniLinePoints() {
      const values =
        this.completedChartValues;

      const source =
        values.length
          ? values
          : [0, 0, 0, 0, 0];

      const sliced =
        source.length > 5
          ? source.slice(-5)
          : source;

      const max =
        Math.max(
          ...sliced,
          1
        );

      return sliced.map(
        (value) =>
          34 -
          Math.round(
            (value / max) * 24
          )
      );
    },

    /* =====================================================
       DONUT
    ====================================================== */

    donutLabels() {
      return [
        this.$t("dashboard.donutCompleted"),
        this.$t("dashboard.donutRemaining"),
      ];
    },

    donutDatasets() {
      const completed =
        Number(this.stats.completed) ||
        0;

      const remaining =
        Math.max(
          0,
          Number(this.stats.total || 0) -
          completed
        );

      return [
        {
          data: [
            completed,
            remaining,
          ],

          backgroundColor: [
            "#43b883",
            "#5458c8",
          ],

          borderWidth: 0,

          hoverBackgroundColor: [
            "#43b883",
            "#5458c8",
          ],
        },
      ];
    },

    donutOptions() {
      return {
        responsive: true,

        maintainAspectRatio: false,

        cutoutPercentage: 72,

        legend: {
          display: false,
        },

        tooltips: {
          enabled: true,
        },
      };
    },

    /* =====================================================
       REPORT FIELDS
    ====================================================== */

    reportFields() {
      return [
        {
          key: "ticketNo",
          label: this.$t("dashboard.field.ticketNo"),
          _classes: "font-weight-bold",
          _style: "min-width:130px;",
        },
        {
          key: "dateInformed",
          label: this.$t("dashboard.field.dateInformed"),
          _style: "min-width:150px;",
        },
        {
          key: "reporterDepartment",
          label: this.$t("dashboard.field.department"),
          _style: "min-width:180px;",
        },
        {
          key: "reporter",
          label: this.$t("dashboard.field.reporterName"),
          _style: "min-width:160px;",
        },
        {
          key: "category",
          label: this.$t("dashboard.field.category"),
          _style: "min-width:180px;",
        },
        {
          key: "status",
          label: this.$t("dashboard.field.status"),
          _style: "min-width:120px;",
        },
        {
          key: "sla",
          label: this.$t("dashboard.field.sla"),
          _style: "min-width:110px;",
        },
        {
          key: "assignee",
          label: this.$t("dashboard.field.assignee"),
          _style: "min-width:160px;",
        },
        {
          key: "closedDate",
          label: this.$t("dashboard.field.closedDate"),
          _style: "min-width:150px;",
        },
      ];
    },

    /* =====================================================
       FORMATTED TABLE
    ====================================================== */

    formattedTickets() {
      return this.filteredTickets.map(
        (ticket) => {
          const subCategory =
            ticket.userPart
              ?.subCategory?.name ||
            ticket.userPart
              ?.subCategory ||
            "-";

          const slaHours =
            this.getSlaHours(
              subCategory
            );

          const deadline = this.getTicketSlaDeadline(ticket);

          const isClosed =
            [
              "done",
              "welldone",
            ].includes(
              ticket.status
            );

          const endDate =
            isClosed
              ? moment(
                ticket?.technicianPart?.dateTimeFinished || ticket.updatedAt
              )
              : moment(this.currentTime);

          const isOverdue =
            deadline && endDate.isAfter(
              deadline
            );

          return {
            ticketNo:
              ticket.ticketNumber ||
              String(
                ticket._id ||
                "N/A"
              )
                .substring(0, 8)
                .toUpperCase(),

            dateInformed:
              this.formatDate(
                ticket.userPart
                  ?.dateTimeInformed ||
                ticket.createdAt
              ),

            reporterDepartment:
              this.getReporterDepartment(
                ticket
              ),

            reporter:
              this.getReporterName(
                ticket
              ),

            category:
              subCategory,

            status:
              this.getStatusLabel(
                ticket.status
              ),

            sla: this.getSlaCountdown(ticket).text,

            assignee:
              this.getAssignee(
                ticket
              ),

            closedDate:
              isClosed
                ? this.formatDate(
                  ticket.updatedAt
                )
                : "-",

            _status_raw:
              ticket.status,

            _sla_hours:
              slaHours,

            _slaOverdue:
              isOverdue,
          };
        }
      );
    },

    /* =====================================================
       PDF
    ====================================================== */

    pdfLocale() {
      return (
        this.$i18n?.locale ||
        "th-TH"
      );
    },

    supervisorSectionName() {
      const user = this.getStoredUser();
      const s = user?.section;
      if (s && typeof s === "object") {
        return s.departmentName || s.name || s.code || "";
      }
      return "";
    },
  },

  watch: {
    /* =====================================================
       RANGE WATCH
    ====================================================== */

    selectedRange() {
      this.todoCurrentPage = 1;
      this.technicianCurrentPage = 1;
      this.partsCurrentPage = 1;
      this.calculateStats();

      if (
        this.selectedTodo &&
        !this.todoList.some(
          (ticket) =>
            ticket._id ===
            this.selectedTodo._id
        )
      ) {
        this.selectedTodo = null;
      }
    },

    filteredTickets: {
      handler() {
        this.technicianCurrentPage = 1;
        this.partsCurrentPage = 1;
        this.calculateStats();
      },

      deep: true,
    },

    todoList() {
      if (this.todoCurrentPage > this.todoTotalPages) {
        this.todoCurrentPage = this.todoTotalPages;
      }
    },

    technicianTotalPages(total) {
      if (this.technicianCurrentPage > total) {
        this.technicianCurrentPage = total;
      }
    },

    searchQuery(val) {
      this.hasNavigatedMatch = false;
      if (val && val.trim()) {
        this.showSearchDropdown = true;
        this.currentMatchIndex = 0;
      } else {
        this.currentMatchIndex = 0;
        this.showSearchDropdown = false;
      }
    },
  },

  mounted() {
    if (this.$route && this.$route.query && this.$route.query.tab) {
      this.activeDashboardTab = this.$route.query.tab;
    }
    this.selectRange(this.selectedRange);
    this.fetchData();
    this.setupSocket();
    this.slaCountdownTimer = setInterval(() => {
      this.currentTime = Date.now();
    }, 60000);
    document.addEventListener('click', this.handleDocumentClick);
  },

  beforeDestroy() {
    this.cleanupSocket();
    if (this.slaCountdownTimer) clearInterval(this.slaCountdownTimer);
    document.removeEventListener('click', this.handleDocumentClick);
  },

  methods: {
    goToTechnicianWorkload() {
      this.activeDashboardTab = 'workload';
      this.activeWorkloadTab = 'technician';
      this.$nextTick(() => {
        const el = document.querySelector('.workload-card');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    removeMatchHighlight() {},

    setMobileTodoFilter(filter) {
      this.mobileTodoFilter = filter;
      this.mobileTodoCurrentPage = 1;
    },

    goToTicketDetail(item) {
      if (!item) return;
      const ticketId = item._id || item.ticketNo || item.ticketNumber || item.number || item.id;
      if (!ticketId || ticketId === '-') return;
      const rawStatus = (item._status_raw || item.status || '').toLowerCase();
      const isFinished = ['done', 'welldone', 'cancel', 'closed'].includes(rawStatus);
      if (isFinished) {
        this.$router.push({ path: '/supervisor/history', query: { ticketId } });
      } else {
        this.$router.push({ path: '/supervisor/mainsupervisor', query: { ticketId } });
      }
    },

    handleDocumentClick(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false;
      }
    },

    clearSearch() {
      this.searchQuery = "";
      this.currentMatchIndex = 0;
      this.hasNavigatedMatch = false;
      this.showSearchDropdown = false;
    },

    closeSearchDropdown() {
      this.showSearchDropdown = false;
    },

    onSelectSearchResult(m) {
      if (!m || !m.ticket) return;
      this.showSearchDropdown = false;
      const ticket = m.ticket;
      const ticketId = ticket._id || ticket.id || '';
      const ticketNo = ticket.ticketNumber || (ticketId ? ticketId.substring(0, 8).toUpperCase() : '');
      const isFinished = ['done', 'welldone', 'cancelled'].includes(ticket.status);

      if (isFinished) {
        this.$router.push({
          path: '/supervisor/history',
          query: { ticketId, ticketNo, highlight: 'true' }
        });
      } else {
        const filter = ticket.status === 'pending' ? 'unassigned' : 'active';
        this.$router.push({
          path: '/supervisor/mainsupervisor',
          query: { filter, ticketId, ticketNo, highlight: 'true' }
        });
      }
    },

    scrollElementToCenter(target) {
      if (!target) return;
      const scrollContainers = [
        document.querySelector('.c-body'),
        document.querySelector('.c-main'),
        document.querySelector('.role-theme-app'),
        document.scrollingElement,
        document.documentElement,
        document.body
      ];
      const container = scrollContainers.find(
        c => c && c.scrollHeight > c.clientHeight && (typeof window === 'undefined' || window.getComputedStyle(c).overflowY !== 'hidden')
      );
      const bottomNavHeight = 90;
      const headerHeight = 70;

      if (container && container !== document.documentElement && container !== document.body) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const currentScroll = container.scrollTop;
        const relativeTargetTop = targetRect.top - containerRect.top + currentScroll;
        const effectiveHeight = Math.max(100, containerRect.height - bottomNavHeight);
        const idealTop = relativeTargetTop - (effectiveHeight / 2) + (targetRect.height / 2);

        container.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      } else {
        const targetRect = target.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const effectiveHeight = Math.max(100, windowHeight - headerHeight - bottomNavHeight);
        const idealTop = (currentScrollY + targetRect.top) - headerHeight - (effectiveHeight / 2) + (targetRect.height / 2);

        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      }
    },

    scrollToAndHighlight(ids) {
      const isVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        if (el.offsetParent === null && (typeof window === 'undefined' || window.getComputedStyle(el).position !== 'fixed')) {
          return false;
        }
        return true;
      };

      let foundTarget = null;
      for (const id of ids) {
        if (!id) continue;
        const rawId = String(id).replace(/^ticket-row-|^m-rep-|^todo-|^m-todo-/, '');
        const candidates = [
          document.getElementById('m-todo-' + rawId),
          document.getElementById('m-rep-' + rawId),
          document.getElementById(id),
          document.getElementById('ticket-row-' + rawId),
          document.getElementById('todo-' + rawId),
          ...(this.$el ? Array.from(this.$el.querySelectorAll(`[data-ticket-id="${rawId}"], [data-ticket-no="${rawId}"]`)) : [])
        ].filter(Boolean);

        for (const el of candidates) {
          const target = el.closest('.mobile-ticket-card') ||
                         el.closest('.task-table-row') ||
                         el.closest('tr') ||
                         el;
          if (isVisible(target)) {
            target.classList.remove('match-highlight-active');
            void target.offsetWidth;
            target.classList.add('match-highlight-active');
            setTimeout(() => {
              target.classList.remove('match-highlight-active');
            }, 3600);
            if (!foundTarget) {
              foundTarget = target;
            }
          }
        }
      }

      if (foundTarget) {
        this.scrollElementToCenter(foundTarget);
        return true;
      }
      return false;
    },

    onSearchEnter() {
      if (this.searchMatches && this.searchMatches.length > 0) {
        this.onSelectSearchResult(this.searchMatches[0]);
      }
    },

    openPartsDetail(ticket) {
      this.selectedPartsTicket = ticket;
      this.showPartsDetailModal = true;
    },

    formatPartsJobType(jobType) {
      const translations = {
        'โสตทัศนูปกรณ์และห้องประชุม': 'Audiovisual Equipment and Meeting Rooms',
      };
      return this.dashboardText(jobType, translations[jobType] || jobType);
    },

    getJobIcon(name) {
      if (!name) return "cil-settings";
      const n = String(name).toLowerCase();
      if (n.includes("cable") || n.includes("tv") || n.includes("เคเบิล") || n.includes("เคเบิ้ล") || n.includes("ทีวี") || n.includes("television")) return "cil-tv";
      if (n.includes("คอมพิวเตอร์") || n.includes("เครื่องคอม") || n.includes("computer") || n.includes("monitor") || n.includes("pc") || n.includes("laptop")) return "cil-monitor";
      if (n.includes("wi-fi") || n.includes("wifi") || n.includes("อินเทอร์เน็ต") || n.includes("เครือข่าย") || n.includes("internet") || n.includes("network") || n.includes("lan")) return "cil-wifi-signal-1";
      if (n.includes("เครื่องพิมพ์") || n.includes("สแกนเนอร์") || n.includes("ปริ้น") || n.includes("printer") || n.includes("scanner") || n.includes("print")) return "cil-print";
      if (n.includes("ปรับอากาศ") || n.includes("เครื่องปรับอากาศ") || n.includes("ระบบเครื่องกล") || n.includes("น้ำเย็น") || n.includes("แอร์") || n.includes("air") || n.includes("cooling")) return "cil-snowflake";
      if (n.includes("หลอดไฟ") || n.includes("แสงสว่าง") || n.includes("lighting") || n.includes("lamp") || n.includes("bulb") || n.includes("โคมไฟ")) return "cil-lightbulb";
      if (n.includes("ปลั๊ก") || n.includes("สวิตช์") || n.includes("เต้ารับ") || n.includes("ไฟฟ้า") || n.includes("electric") || n.includes("power")) return "cil-bolt";
      if (n.includes("ท่อ") || n.includes("ก๊อก") || n.includes("น้ำรั่ว") || n.includes("น้ำไม่ไหล") || n.includes("น้ำไม่") || n.includes("ประปา") || n.includes("สุขาภิบาล") || n.includes("ชักโครก") || n.includes("plumb") || n.includes("sanitation") || n.includes("water") || n.includes("leak") || n.includes("drain") || n.includes("tap") || n.includes("toilet")) return "cil-drop";
      if (n.includes("ลิฟต์") || n.includes("ลิฟท์") || n.includes("ประตู") || n.includes("หน้าต่าง") || n.includes("ลูกบิด") || n.includes("กลอน") || n.includes("กุญแจ") || n.includes("door") || n.includes("elevator") || n.includes("window")) return "cil-door";
      if (n.includes("อาคาร") || n.includes("โยธา") || n.includes("สถาปัตย์") || n.includes("ผนัง") || n.includes("พื้น") || n.includes("เพดาน") || n.includes("ร้าว") || n.includes("หลุดร่อน") || n.includes("กระเบื้อง") || n.includes("building") || n.includes("civil") || n.includes("wall") || n.includes("floor")) return "cil-building";
      if (n.includes("โทรศัพท์") || n.includes("phone") || n.includes("telephone")) return "cil-phone";
      if (n.includes("กล้อง") || n.includes("cctv") || n.includes("camera")) return "cil-camera";
      if (n.includes("เฟอร์นิเจอร์") || n.includes("ครุภัณฑ์") || n.includes("วัสดุ") || n.includes("โต๊ะ") || n.includes("เก้าอี้") || n.includes("furniture") || n.includes("couch") || n.includes("chair") || n.includes("desk")) return "cil-couch";
      return "cil-settings";
    },

    applyHotKeyFilter(item) {
      // Static display - does not bounce or scroll
    },

    clearHotKeyFilter() {
      this.selectedHotKey = "";
      if (this.searchQuery) {
        this.searchQuery = "";
      }
    },

    syncHotKeysToUserForm() {
      try {
        const topNames = this.top5RepairRequests.map(i => i.name);
        localStorage.setItem("smartfix_top_hotkeys", JSON.stringify(topNames));
        Swal.fire({
          icon: "success",
          title: this.dashboardText("ซิงค์ Hot Key สำเร็จ", "Hot Keys Synced"),
          text: this.dashboardText(
            "หมวดหมู่ Top 5 ได้ถูกเชื่อมโยงเป็นปุ่มลัด (Hot Key) ในหน้าแจ้งซ่อมของผู้ใช้เรียบร้อยแล้ว",
            "Top 5 categories are now linked as Hot Keys in requester reporting."
          ),
          timer: 2200,
          showConfirmButton: false
        });
      } catch (e) {
        console.error(e);
      }
    },

    dashboardText(thaiText, englishText) {
      const locale = String(
        this.$i18n?.locale || this.$store?.state?.locale?.lang || "th"
      ).toLowerCase();

      return locale.startsWith("th") ? thaiText : englishText;
    },

    /* =====================================================
       RANGE PRESET
    ====================================================== */

    selectRange(range) {
      const now = moment();

      this.selectedRange = range;

      switch (range) {
        case "today":
          this.startDate = now.clone().startOf("day").format("YYYY-MM-DD");
          this.endDate = now.clone().endOf("day").format("YYYY-MM-DD");
          break;

        case "week":
          this.startDate = now.clone().startOf("isoWeek").format("YYYY-MM-DD");
          this.endDate = now.clone().endOf("isoWeek").format("YYYY-MM-DD");
          break;

        case "month":
          this.startDate = now.clone().startOf("year").format("YYYY-MM-DD");
          this.endDate = now.clone().endOf("year").format("YYYY-MM-DD");
          break;

        case "year":
          this.startDate = now.clone().subtract(4, "years").startOf("year").format("YYYY-MM-DD");
          this.endDate = now.clone().endOf("year").format("YYYY-MM-DD");
          break;

        case "custom":
          break;

        case "all":
          this.startDate = "";
          this.endDate = "";
          break;
      }
    },

    /* =====================================================
       FETCH DASHBOARD
    ====================================================== */

    async fetchData() {
      try {
        const user =
          this.getStoredUser();

        /*
         * หลัง schema refactor:
         *
         * Supervisor visibility ใช้ section
         * ไม่ใช่ department
         *
         * แต่รองรับทั้ง object และ id
         */
        const sectionId =
          this.extractId(
            user?.section || user?.sectionId || user?.section_id
          );

        if (!sectionId) {
          console.warn(
            "[SupervisorDashboard] Supervisor section not found"
          );

          this.tickets = [];
          this.categories = [];
          this.technicians = [];

          this.calculateStats();

          return;
        }

        const [
          tickets,
          categories,
          technicians,
        ] =
          await Promise.all([
            this.fetchSupervisorTickets(
              sectionId
            ),

            this.fetchCategories(),

            this.fetchTechnicians(),
          ]);

        await this.fetchCurrentRepairWorkload();

        this.tickets =
          Array.isArray(tickets)
            ? tickets
              .filter(Boolean)
              .sort(
                (a, b) =>
                  this.getTicketDate(
                    b
                  ).valueOf() -
                  this.getTicketDate(
                    a
                  ).valueOf()
              )
            : [];

        this.categories =
          Array.isArray(categories)
            ? categories
            : [];

        this.technicians =
          Array.isArray(technicians)
            ? technicians
            : [];

        this.calculateStats();
      } catch (error) {
        console.error(
          "[SupervisorDashboard] fetchData error:",
          error
        );

        this.tickets = [];
        this.categories = [];
        this.technicians = [];

        this.calculateStats();
      }
    },

    /* =====================================================
       FETCH SUPERVISOR TICKETS
    ====================================================== */

    async fetchSupervisorTickets(
      sectionId
    ) {
      /*
       * ใช้ Service.ticket เดิมของโปรเจกต์
       *
       * Route หลัง refactor ควร resolve
       * Supervisor scope จาก authenticated user.section
       *
       * urlAppend นี้เก็บรูปแบบเดิมไว้เพื่อไม่สร้าง
       * service ใหม่ในหน้า Dashboard
       */

      try {
        const response =
          await Service.ticket(
            "get",
            null,
            {
              urlAppend:
                `/supervisor/${sectionId}/all`,
            }
          );

        return this.extractArrayFromResponse(
          response
        );
      } catch (error) {
        console.error(
          "[SupervisorDashboard] Failed to fetch supervisor tickets:",
          error
        );

        return [];
      }
    },

    /* =====================================================
       FETCH CATEGORY
    ====================================================== */

    async fetchCategories() {
      try {
        const response =
          await Service.category(
            "get"
          );

        return this.extractArrayFromResponse(
          response
        );
      } catch (error) {
        console.error(
          "[SupervisorDashboard] Failed to fetch categories:",
          error
        );

        return [];
      }
    },

    async fetchTechnicians() {
      try {
        const response = await Service.user("get", null, {
          urlAppend: "/technicians",
        });
        return this.extractArrayFromResponse(response);
      } catch (error) {
        console.error("[SupervisorDashboard] Failed to fetch technicians:", error);
        return [];
      }
    },

    async fetchCurrentRepairWorkload() {
      this.workloadLoading = true;
      this.workloadError = null;

      try {
        const response = await Service.ticket("get", null, {
          urlAppend: "/workload/current",
        });

        const rows = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        this.currentRepairWorkloads = rows.map((worker) => ({
          ...worker,
          workloadPercent: Number(worker?.workloadPercent) || 0,
          activeTicketCount: Number(worker?.activeTicketCount) || 0,
        }));
      } catch (error) {
        console.error("[SupervisorDashboard] Failed to fetch current repair workload:", error);
        this.workloadError = error;
        this.currentRepairWorkloads = [];
      } finally {
        this.workloadLoading = false;
      }
    },

    getActiveAssigneeIds(ticket) {
      const assignments = ticket?.supervisorPart?.assignments;

      if (Array.isArray(assignments) && assignments.length) {
        return assignments
          .filter((assignment) => assignment && assignment.status !== "rejected")
          .map((assignment) => this.extractId(assignment.assignee))
          .filter(Boolean)
          .map(String);
      }

      const legacyAssigneeId = this.extractId(
        ticket?.supervisorPart?.assignedTechnician
      );
      return legacyAssigneeId ? [String(legacyAssigneeId)] : [];
    },

    getAssignmentStatusForUser(ticket, userId) {
      const assignments = ticket?.supervisorPart?.assignments;
      if (Array.isArray(assignments) && assignments.length) {
        const assignment = assignments.find((item) =>
          item && String(this.extractId(item.assignee)) === String(userId)
        );
        return assignment?.status || "";
      }

      return String(this.extractId(ticket?.supervisorPart?.assignedTechnician)) === String(userId)
        ? "accepted"
        : "";
    },

    calculateCompletionRate(completed, assigned) {
      const total = Number(assigned) || 0;
      if (total <= 0) return 0;
      return Math.min(100, Math.max(0, Math.round(((Number(completed) || 0) / total) * 100)));
    },

    /* =====================================================
       API RESPONSE HELPER
    ====================================================== */

    extractArrayFromResponse(
      response
    ) {
      const payload =
        response?.data;

      if (
        Array.isArray(payload)
      ) {
        return payload;
      }

      if (
        Array.isArray(
          payload?.data
        )
      ) {
        return payload.data;
      }

      if (
        Array.isArray(
          payload?.tickets
        )
      ) {
        return payload.tickets;
      }

      if (
        Array.isArray(
          payload?.result
        )
      ) {
        return payload.result;
      }

      return [];
    },

    /* =====================================================
       STORED USER
    ====================================================== */

    getStoredUser() {
      /*
       * พยายามใช้ Vuex ก่อน
       */
      const storeUser =
        this.$store?.getters?.["auth/profile"] ||
        this.$store?.getters?.["auth/user"] ||
        this.$store?.state?.auth?.profile ||
        this.$store?.getters?.["user/user"] ||
        this.$store?.state?.user?.user;
      if (
        storeUser &&
        typeof storeUser === "object" &&
        (storeUser._id || storeUser.id || storeUser.name || storeUser.email)
      ) {
        return storeUser;
      }

      /*
       * fallback localStorage
       */
      try {
        const raw =
          localStorage.getItem(
            "user"
          ) ||
          localStorage.getItem(
            "objs"
          );

        if (!raw) {
          return null;
        }

        return JSON.parse(raw);
      } catch (error) {
        console.warn(
          "[SupervisorDashboard] Cannot parse local user:",
          error
        );

        return null;
      }
    },

    extractId(value) {
      if (!value) {
        return "";
      }

      if (
        typeof value ===
        "string"
      ) {
        return value;
      }

      if (
        typeof value ===
        "object"
      ) {
        return (
          value._id ||
          value.id ||
          ""
        );
      }

      return "";
    },

    /* =====================================================
       CUSTOM DATE
    ====================================================== */

    /* =====================================================
       ON RANGE SELECT — เมื่อกด hotkey หรือ custom filter
    ====================================================== */

    onRangeSelect(rangeValue) {
      const today = moment()

      if (rangeValue === 'custom') {
        // toggle: ถ้ากด custom ครั้งแรกให้ clear date
        if (this.selectedRange !== 'custom') {
          this.startDate = ''
          this.endDate = ''
        }
        this.selectedRange = 'custom'
        return
      }

      if (rangeValue === 'all') {
        this.startDate = ''
        this.endDate = ''
        this.selectedRange = 'all'
        return
      }

      // set startDate/endDate ให้ตรงกับ hotkey ที่กด
      switch (rangeValue) {
        case 'today':
          this.startDate = today.format('YYYY-MM-DD')
          this.endDate = today.format('YYYY-MM-DD')
          break
        case 'week':
          this.startDate = today.clone().startOf('isoWeek').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('isoWeek').format('YYYY-MM-DD')
          break
        case 'month':
          this.startDate = today.clone().startOf('month').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('month').format('YYYY-MM-DD')
          break
        case 'year':
          this.startDate = today.clone().startOf('year').format('YYYY-MM-DD')
          this.endDate = today.clone().endOf('year').format('YYYY-MM-DD')
          break
      }

      this.selectedRange = rangeValue
    },

    onCustomDateChange() {
      if (
        !this.startDate ||
        !this.endDate
      ) {
        return;
      }

      const start =
        moment(this.startDate);

      const end =
        moment(this.endDate);

      if (
        end.isBefore(
          start,
          "day"
        )
      ) {
        this.endDate =
          this.startDate;
      }

      this.calculateStats();
    },

    /* =====================================================
       STATS
    ====================================================== */

    calculateStats() {
      const tickets =
        this.filteredTickets;

      const inProgress =
        tickets.filter(
          (ticket) =>
            [
              "pending",
              "assigned",
              "in_progress",
            ].includes(
              ticket.status
            )
        ).length;

      const completed =
        tickets.filter(
          (ticket) =>
            [
              "done",
              "welldone",
            ].includes(
              ticket.status
            )
        ).length;

      const overdue =
        tickets.filter(
          (ticket) =>
            this.isTicketOverdue(
              ticket
            )
        ).length;

      /*
       * งานที่ช่างทำเสร็จแล้ว
       * แต่ยังไม่มี rating
       */
      const awaitingEvaluation =
        tickets.filter(
          (ticket) => {
            const finished =
              [
                "done",
                "welldone",
              ].includes(
                ticket.status
              );

            const rating =
              Number(
                ticket.rating
                  ?.stars
              );

            return (
              finished &&
              !(rating > 0)
            );
          }
        ).length;

      const ratedTickets =
        tickets.filter(
          (ticket) =>
            Number(
              ticket.rating
                ?.stars
            ) > 0
        );

      let satisfaction = 0;

      if (
        ratedTickets.length
      ) {
        const totalRating =
          ratedTickets.reduce(
            (
              total,
              ticket
            ) =>
              total +
              Number(
                ticket.rating
                  .stars
              ),
            0
          );

        satisfaction =
          (
            totalRating /
            ratedTickets.length
          ).toFixed(1);
      }

      this.stats = {
        total:
          tickets.length,

        inProgress,

        completed,

        overdue,

        satisfaction,

        awaitingEvaluation,
      };
    },

    /* =====================================================
       BUILD CHART VALUES
    ====================================================== */

    buildChartValues(
      tickets
    ) {
      const labels =
        this.highlightLabels;

      const values =
        new Array(
          labels.length
        ).fill(0);

      if (
        !labels.length
      ) {
        return values;
      }

      tickets.forEach(
        (ticket) => {
          const date =
            this.getTicketDate(
              ticket
            );

          if (
            !date.isValid()
          ) {
            return;
          }

          let index = -1;

          switch (
          this.selectedRange
          ) {
            case "today":
              index =
                Math.floor(
                  date.hour() /
                  3
                );
              break;

            case "week":
              index =
                date.isoWeekday() -
                1;
              break;

            case "month":
              index =
                date.month();
              break;

            case "year":
            case "all": {
              const startYear = moment().year() - 4;
              index = date.year() - startYear;
              break;
            }

            case "custom":
              index =
                this.getCustomChartIndex(
                  date
                );
              break;
          }

          if (
            index >= 0 &&
            index <
            values.length
          ) {
            values[index] +=
              1;
          }
        }
      );

      return values;
    },

    getCustomChartIndex(
      date
    ) {
      if (
        !this.startDate ||
        !this.endDate
      ) {
        return -1;
      }

      const start =
        moment(
          this.startDate
        ).startOf("day");

      const end =
        moment(
          this.endDate
        ).startOf("day");

      const difference =
        end.diff(
          start,
          "days"
        );

      if (
        difference <= 31
      ) {
        return (
          date
            .clone()
            .startOf("day")
            .diff(
              start,
              "days"
            )
        );
      }

      const monthStart =
        start
          .clone()
          .startOf("month");

      return (
        date.year() -
        monthStart.year()
      ) *
        12 +
        (
          date.month() -
          monthStart.month()
        );
    },

    /* =====================================================
       DATE
    ====================================================== */

    getTicketDate(ticket) {
      const value =
        ticket?.userPart
          ?.dateTimeInformed ||
        ticket?.createdAt ||
        ticket?.updatedAt;

      return moment(value);
    },

    formatDate(date) {
      if (!date) {
        return "-";
      }

      const parsed =
        moment(date);

      if (
        !parsed.isValid()
      ) {
        return "-";
      }

      return parsed.format(
        "DD/MM/YYYY HH:mm"
      );
    },

    /* =====================================================
       SLA
    ====================================================== */

    getTicketParts(ticket) {
      const technicianPart = ticket?.technicianPart || {};

      if (Array.isArray(technicianPart.parts) && technicianPart.parts.length) {
        return technicianPart.parts
          .filter((part) => part && String(part.name || "").trim())
          .map((part) => {
            const quantity = Math.max(1, Number(part.quantity) || 1);
            const unitPrice = Math.max(0, Number(part.unitPrice) || 0);
            return {
              name: String(part.name).trim(),
              quantity,
              unitPrice,
              total: quantity * unitPrice,
            };
          });
      }

      const legacyName = String(technicianPart.spareParts || "").trim();
      if (legacyName && legacyName !== "-") {
        const quantity = Math.max(1, Number(technicianPart.quantity) || 1);
        const unitPrice = Math.max(0, Number(technicianPart.cost) || 0);
        return [{ name: legacyName, quantity, unitPrice, total: quantity * unitPrice }];
      }

      if (Number(technicianPart.cost) > 0) {
        const cost = Number(technicianPart.cost);
        return [{ name: 'ค่าใช้จ่ายทั่วไป', quantity: 1, unitPrice: cost, total: cost }];
      }

      return [];
    },

    formatCurrency(value) {
      const locale = String(this.$i18n?.locale || "th-TH").startsWith("th") ? "th-TH" : "en-US";
      return Number(value || 0).toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    getTicketSlaDeadline(ticket) {
      const storedDeadline = moment(ticket?.slaDeadline);
      if (ticket?.slaDeadline && storedDeadline.isValid()) return storedDeadline;
      const subCategory = ticket?.userPart?.subCategory?.name || ticket?.userPart?.subCategory;
      const start = moment(ticket?.userPart?.dateTimeInformed || ticket?.createdAt);
      if (!start.isValid()) return null;
      return start.clone().add(this.getSlaHours(subCategory), "hours");
    },

    formatSlaDuration(milliseconds) {
      const totalMinutes = Math.max(0, Math.ceil(Math.abs(milliseconds) / 60000));
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;
      const values = [];
      if (days) values.push(days + " " + this.dashboardText("วัน", "d"));
      if (hours) values.push(hours + " " + this.dashboardText("ชม.", "h"));
      if (minutes || !values.length) values.push(minutes + " " + this.dashboardText("นาที", "m"));
      return values.slice(0, 2).join(String.fromCharCode(32));
    },

    getSlaCountdown(ticket) {
      const deadline = this.getTicketSlaDeadline(ticket);
      if (!deadline) {
        return { text: this.dashboardText("ไม่มีข้อมูล SLA", "No SLA data"), className: "sla-neutral" };
      }

      const closed = ["done", "welldone"].includes(ticket?.status);
      const finishedAt = moment(ticket?.technicianPart?.dateTimeFinished || ticket?.updatedAt);
      const comparisonTime = closed && finishedAt.isValid() ? finishedAt : moment(this.currentTime);
      const difference = deadline.diff(comparisonTime);

      if (closed) {
        return difference >= 0
          ? { text: this.dashboardText("เสร็จก่อน SLA", "Completed within SLA"), className: "sla-safe" }
          : { text: this.dashboardText("เสร็จเกิน SLA", "Completed over SLA"), className: "sla-overdue" };
      }

      return this.getOpenTicketSlaCountdown(ticket, deadline, difference);
    },

    getOpenTicketSlaCountdown(ticket, deadline, difference) {
      if (difference < 0) {
        return {
          text: this.dashboardText("เกิน SLA", "Over SLA by") + " " + this.formatSlaDuration(difference),
          className: "sla-overdue",
        };
      }

      const start = moment(ticket?.userPart?.dateTimeInformed || ticket?.createdAt);
      const totalDuration = start.isValid() ? Math.max(1, deadline.diff(start)) : 1;
      const ratio = difference / totalDuration;
      const className = ratio <= 0.25 ? "sla-urgent" : ratio <= 0.5 ? "sla-warning" : "sla-safe";

      return {
        text: this.dashboardText("เหลือ", "Remaining") + " " + this.formatSlaDuration(difference),
        className,
      };
    },

    getSlaHours(
      subCategory
    ) {
      if (!subCategory) {
        return 24;
      }

      for (
        const category of
        this.categories
      ) {
        const list =
          Array.isArray(
            category
              ?.subCategories
          )
            ? category
              .subCategories
            : [];

        const found =
          list.find(
            (item) =>
              item?.name ===
              subCategory
          );

        if (
          found &&
          Number(
            found.slaHours
          ) > 0
        ) {
          return Number(
            found.slaHours
          );
        }
      }

      return 24;
    },

    isTicketOverdue(
      ticket
    ) {
      const storedDeadline = moment(ticket?.slaDeadline);

      if (ticket?.slaDeadline && storedDeadline.isValid()) {
        const isClosed = ["done", "welldone"].includes(ticket.status);
        const end = isClosed
          ? moment(ticket?.technicianPart?.dateTimeFinished || ticket.updatedAt)
          : moment();

        return end.isValid() && end.isAfter(storedDeadline);
      }

      const subCategory =
        ticket?.userPart
          ?.subCategory?.name ||
        ticket?.userPart
          ?.subCategory;

      const slaHours =
        this.getSlaHours(
          subCategory
        );

      const start =
        this.getTicketDate(
          ticket
        );

      if (
        !start.isValid()
      ) {
        return false;
      }

      const deadline =
        start
          .clone()
          .add(
            slaHours,
            "hours"
          );

      const isClosed =
        [
          "done",
          "welldone",
        ].includes(
          ticket.status
        );

      const end =
        isClosed
          ? moment(
            ticket?.technicianPart?.dateTimeFinished || ticket.updatedAt
          )
          : moment();

      return end.isAfter(
        deadline
      );
    },

    /* =====================================================
       REPORTER
    ====================================================== */

    getReporterName(
      ticket
    ) {
      const user =
        ticket?.userPart
          ?.user;

      if (
        user &&
        typeof user ===
        "object"
      ) {
        const fullname =
          `${user.name || ""} ${user.surname ||
            ""
            }`.trim();

        if (fullname) {
          return fullname;
        }

        if (
          user.fullname
        ) {
          return user.fullname;
        }

        if (user.email) {
          return user.email;
        }
      }

      return (
        ticket?.userPart
          ?.fullname ||
        ticket?.userPart
          ?.username ||
        "-"
      );
    },

    getReporterDepartment(
      ticket
    ) {
      return getTicketReporterAffiliation(ticket);
    },

    getTicketReporterAffiliation,

    getTaskCategory(ticket) {
      const sub = ticket?.userPart?.subCategory;
      if (!sub) return "-";
      if (typeof sub === "object") {
        return sub.name || "-";
      }
      return String(sub);
    },

    /* =====================================================
       ASSIGNEE
    ====================================================== */

    getAssignee(ticket) {
      const technician =
        ticket
          ?.supervisorPart
          ?.assignedTechnician;

      if (
        !technician
      ) {
        return "-";
      }

      if (
        typeof technician ===
        "object"
      ) {
        const name =
          `${technician.name ||
            ""
            } ${technician.surname ||
            ""
            }`.trim();

        return (
          name ||
          technician.email ||
          "-"
        );
      }

      return "-";
    },

    /* =====================================================
       LOCATION
    ====================================================== */

    getTaskLocation(
      ticket
    ) {
      const userPart =
        ticket?.userPart ||
        {};

      const building =
        userPart.building;

      let buildingName =
        "";

      if (
        building &&
        typeof building ===
        "object"
      ) {
        buildingName =
          building.name ||
          building
            .buildingName ||
          "";
      } else if (
        typeof building ===
        "string"
      ) {
        buildingName =
          building;
      }

      const room =
        userPart.roomName ||
        userPart.room ||
        "";

      const location =
        [
          buildingName,
          room,
        ]
          .filter(Boolean)
          .join(" / ");

      return (
        location ||
        userPart
          .locationType ||
        "-"
      );
    },

    /* =====================================================
       STATUS
    ====================================================== */

    getStatusLabel(
      status
    ) {
      if (!status) {
        return "-";
      }

      const key =
        `dashboard.status.${status}`;

      const translated =
        this.$t(key);

      if (
        translated &&
        translated !== key
      ) {
        return translated;
      }

      const fallback = {
        pending:
          "รอดำเนินการ",

        assigned:
          "มอบหมายช่าง",

        in_progress:
          "กำลังดำเนินการ",

        done:
          "เสร็จสิ้น",

        welldone:
          "ปิดงานแล้ว",

        cancelled:
          "ยกเลิก",

        rejected:
          "ปฏิเสธ",
      };

      return (
        fallback[status] ||
        status
      );
    },

    translateOrFallback(
      key,
      fallback
    ) {
      const value =
        this.$t(key);

      return (
        value &&
          value !== key
          ? value
          : fallback
      );
    },

    /* =====================================================
       TODO SELECTION
    ====================================================== */

    selectTodo(ticket) {
      if (window.innerWidth <= 768) {
        this.$router.push('/supervisor/mainsupervisor');
        return;
      }
      this.selectedTodo = ticket;
    },

    /* =====================================================
       ASSIGN TECHNICIAN
    ====================================================== */

    assignTechnician(
      ticket
    ) {
      if (
        !ticket?._id
      ) {
        return;
      }

      /*
       * ไม่สร้าง assignment logic ใหม่
       * เพราะหน้า Report Detail / Report List
       * ของโปรเจกต์มี flow เดิมอยู่แล้ว
       *
       * ส่งไปยังหน้ารายการงานเดิม
       */
      this.$router.push({
        path:
          "/supervisor/mainsupervisor",

        query: {
          ticketId:
            ticket._id,
        },
      });
    },

    /* =====================================================
       SOCKET
    ====================================================== */

    setupSocket() {
      try {
        this.socket =
          getSocket();

        if (!this.socket) {
          return;
        }

        /*
         * เมื่อ ticket มีการเปลี่ยนแปลง
         * reload dashboard data
         */
        this.socket.on(
          "ticketCreated",
          this.handleSocketRefresh
        );

        this.socket.on(
          "ticketUpdated",
          this.handleSocketRefresh
        );

        this.socket.on(
          "ticketAssigned",
          this.handleSocketRefresh
        );

        this.socket.on(
          "ticketStatusUpdated",
          this.handleSocketRefresh
        );
      } catch (error) {
        console.warn(
          "[SupervisorDashboard] Socket setup failed:",
          error
        );
      }
    },

    cleanupSocket() {
      if (!this.socket) {
        return;
      }

      try {
        this.socket.off(
          "ticketCreated",
          this.handleSocketRefresh
        );

        this.socket.off(
          "ticketUpdated",
          this.handleSocketRefresh
        );

        this.socket.off(
          "ticketAssigned",
          this.handleSocketRefresh
        );

        this.socket.off(
          "ticketStatusUpdated",
          this.handleSocketRefresh
        );
      } catch (error) {
        console.warn(
          "[SupervisorDashboard] Socket cleanup failed:",
          error
        );
      }
    },

    handleSocketRefresh() {
      this.fetchData();
      this.fetchCurrentRepairWorkload();
    },

    getBadge(status) {
      const colors = {
        pending: "warning",
        assigned: "info",
        in_progress: "primary",
        done: "success",
        welldone: "success",
        cancelled: "danger",
        rejected: "danger",
      };
      return colors[status] || "secondary";
    },
  },
};
</script>

<style scoped>

/* =====================================================
   ROOT
===================================================== */

.supervisor-dashboard {
  --admin-primary: #1d4ed8;
  --admin-dark: #1e3a8a;
  --admin-indigo: #1e40af;
  --success: #059669;
  --warning: #d97706;
  --danger: #dc2626;
  --rating: #f59e0b;
  --purple: #6f42c1;

  --page-bg: #eff6ff;
  --surface: #ffffff;

  --border: #bfdbfe;
  --border-soft: #dbeafe;

  --text: #0f172a;
  --muted: #64748b;

  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 0;
  padding: 6px 12px 28px;
  background: #eff6ff !important;
  color: var(--text);

  font-family: "Sarabun", sans-serif;
  font-size: 14px;
  font-weight: 400;

  box-sizing: border-box;
}
.admin-dashboard-root {
  width: 100%;
  max-width: 100%;
}

.dashboard-sticky-area {
  position: sticky !important;
  top: var(--app-header-height) !important;
  z-index: 100 !important;
  margin: 0 0 16px 0 !important;
  padding: 0 !important;
  width: 100%;
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  overflow: visible;
}

/* =====================================================
   DASHBOARD HEADER
===================================================== */

.dashboard-topbar {
  position: static;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 112px;
  min-height: 112px;
  margin: 0;
  padding: 22px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #1e40af 100%) !important;
  border-radius: 16px !important;
  overflow: visible !important;
  box-shadow: 0 4px 18px rgba(30, 58, 138, 0.25) !important;
  box-sizing: border-box;
}

.dashboard-page-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff !important;
  font-size: clamp(1.4rem, 2vw, 1.9rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
}

.dashboard-title-icon,
.dashboard-page-title svg,
::v-deep .dashboard-page-title .c-icon {
  color: #ffffff !important;
  fill: currentColor !important;
  font-size: 1.6rem;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

/* =====================================================
   FILTER
===================================================== */

.dashboard-filter {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  max-width: 100%;
}

.range-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
}

.range-tab {
  padding: 8px 16px;
  font-size: 0.92rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.range-tab:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.range-tab.active {
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 800;
  border-color: #ffffff;
}

.custom-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 8px;
  border: none;
}

.custom-date-range input {
  border: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #0f172a;
}

.custom-date-range span {
  color: #777777;
  font-size: 0.85rem;
}

.custom-date-range .range-tab {
  min-height: 28px;
  padding: 4px 8px;
  border: 0;
  border-left: 1px solid #dddddd;
  color: #1d4ed8;
  border-radius: 0;
}

.filter-tab {
  padding: 8px 12px;
  font-size: 16px;
}

.filter-tab,
.filter-tab svg,
::v-deep .filter-tab .c-icon {
  color: #ffffff !important;
}

.filter-tab.active,
.filter-tab.active svg,
::v-deep .filter-tab.active .c-icon {
  color: #1d4ed8 !important;
}

/* =====================================================
   KPI AREA
===================================================== */

.kpi-grid {
  width: 100%;
  min-width: 0;

  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));

  gap: 16px;

  margin-bottom: 16px;

  box-sizing: border-box;
}

/* =====================================================
   KPI CARD
===================================================== */

.summary-card {
  position: relative;
  width: 100%;
  min-width: 0;
  background: #ffffff !important;
  border: 1.5px solid #dbeafe !important;
  padding: 20px 22px !important;
  border-radius: 16px !important;
  box-shadow: 0 2px 8px rgba(30, 58, 138, 0.04) !important;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease !important;
  box-sizing: border-box;
  overflow: hidden;
  cursor: pointer;
}

.summary-card:hover {
  transform: translateY(-2px) !important;
  border-color: #93c5fd !important;
  box-shadow: 0 6px 18px rgba(30, 58, 138, 0.1) !important;
}

.summary-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.icon-blue {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid #bfdbfe !important;
}

.icon-orange {
  background: #faf5ff !important;
  color: #7c3aed !important;
  border: 1px solid #e9d5ff !important;
}

.icon-red {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

.icon-green {
  background: #f0fdf4 !important;
  color: #059669 !important;
  border: 1px solid #bbf7d0 !important;
}

.icon-indigo {
  background: #eef2ff !important;
  color: #4f46e5 !important;
  border: 1px solid #c7d2fe !important;
}

.summary-tech-pills {
  display: flex;
  align-items: center;
  gap: 4px;
}

.tech-pill {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.pill-free {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.pill-busy {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.summary-more {
  color: #bbbbbb;
  font-size: 13px;
  line-height: 1;
  letter-spacing: 1px;
}

.summary-label {
  color: #0f172a !important;
  font-size: 1.02rem !important;
  font-weight: 800 !important;
  margin-bottom: 6px;
  font-family: "Sarabun", sans-serif;
}

.summary-bottom {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  min-width: 0;
  margin-top: 5px;
  gap: 8px;
}

.summary-value {
  font-size: 2.3rem !important;
  font-weight: 800 !important;
  color: #0f172a !important;
  line-height: 1;
  font-family: "Sarabun", sans-serif;
}

.trend-badge {
  padding: 4px 10px !important;
  border-radius: 999px !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
}

.trend-neutral {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid #bfdbfe !important;
}

.trend-active {
  background: #faf5ff !important;
  color: #7c3aed !important;
  border: 1px solid #e9d5ff !important;
}

.trend-down {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

.trend-up {
  background: #f0fdf4 !important;
  color: #059669 !important;
  border: 1px solid #bbf7d0 !important;
}

/* OPERATIONAL SUMMARY */
.operational-summary {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
}

.overview-panel {
  flex: 1;
  min-width: 0;
  background: #ffffff !important;
  padding: 16px 22px !important;
  border-radius: 14px !important;
  border: 2px solid #e0f2fe !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.overview-panel.clickable {
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.overview-panel.clickable:hover {
  border-color: #38bdf8 !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(3, 105, 161, 0.12) !important;
}

.overview-panel.clickable:active {
  transform: translateY(0);
}

.overview-panel>div:first-child,
.overview-panel-secondary {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.overview-eyebrow {
  display: block;
  font-size: 0.85rem !important;
  color: #0369a1 !important;
  text-transform: uppercase;
  font-weight: 800 !important;
  margin-bottom: 3px;
}

.overview-panel strong {
  font-size: 1.3rem !important;
  color: #0f172a !important;
  font-weight: 800 !important;
}

.overview-metrics {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.overview-metric {
  font-size: 0.88rem !important;
  padding: 5px 12px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  color: #475569 !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.overview-metric b {
  color: #0f172a !important;
  font-weight: 800 !important;
  font-size: 1.05rem !important;
  margin-right: 4px;
}

/* ZERO-SCROLL DASHBOARD MAIN TABS */
.dashboard-main-tabs {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  padding: 6px 4px 12px;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.main-tab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 22px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e40af;
  background: #ffffff;
  border: 1.5px solid #bfdbfe;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(29, 78, 216, 0.08);

  &:hover {
    background: #eff6ff;
    border-color: #60a5fa;
    color: #1d4ed8;
    transform: translateY(-1px);
  }

  &.active {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%) !important;
    color: #ffffff !important;
    border-color: #1d4ed8 !important;
    box-shadow: 0 4px 16px rgba(29, 78, 216, 0.35) !important;
    font-weight: 800;
  }
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  margin-left: 8px;
  background: #dbeafe;
  color: #1d4ed8;
}

.main-tab-btn.active .tab-badge {
  background: #ffffff;
  color: #1e40af;
}

/* SEGMENTED TAB WORKLOAD */
.workload-tab-segmented {
  display: inline-flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
  gap: 4px;
  border: 1px solid #cbd5e1;
}

.segmented-btn {
  border: none;
  background: transparent;
  padding: 8px 16px;
  font-size: 0.92rem;
  font-weight: 700;
  color: #475569;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: #1e40af;
  }

  &.active {
    background: #ffffff;
    color: #1e40af;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
}

/* =====================================================
   ANALYTICS
===================================================== */

.analytics-layout {
  width: 100%;
  min-width: 0;

  display: grid;

  grid-template-columns: 2fr 1fr;

  gap: 16px;

  margin-bottom: 16px;

  align-items: stretch;
}

.analytics-card {
  position: relative;

  width: 100%;
  min-width: 0;

  background: #ffffff;

  padding: 18px 20px;
  border: 1px solid var(--border-soft);
  border-radius: 12px;

  overflow: hidden;

  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
}

.analytics-card-header {
  width: 100%;
  min-width: 0;
  margin-bottom: 12px;
}

.analytics-card-header>div {
  min-width: 0;
}

.analytics-card-header h4 {
  margin: 0;

  color: var(--admin-primary);
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.analytics-card-header p {
  margin: 2px 0 0;

  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 400;
}

.icon-button {
  width: 29px;
  height: 29px;

  flex: 0 0 29px;

  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 3px;

  background: transparent;
  color: #ffffff;

  cursor: pointer;
}

.icon-button:hover {
  background: rgba(255, 255, 255, 0.12);
}

.main-chart-card {
  min-width: 0;
}

.highlight-chart-wrapper {
  position: relative;
  width: 100%;
  min-width: 0;
  height: auto;
  min-height: 280px;
  padding: 12px 16px;
  overflow: visible;
  background: #ffffff;
  box-sizing: border-box;
}

.highlight-chart-wrapper>div {
  position: relative !important;

  width: 100% !important;
  height: 100% !important;

  max-width: 100% !important;
  max-height: 100% !important;
}

::v-deep .highlight-chart-wrapper canvas {
  display: block !important;

  width: 100% !important;
  height: 100% !important;

  max-width: 100% !important;
  max-height: 100% !important;
}

.analytics-side {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 14px;
}

.mini-stats {
  min-width: 0;

  display: grid;

  grid-template-columns:
    repeat(2, minmax(0, 1fr));

  gap: 14px;
}

.mini-stats .summary-card {
  height: 140px;
}

/* =====================================================
   PERFORMANCE
===================================================== */

.performance-card {
  flex: 1;
  min-width: 0;
}

.performance-content {
  min-height: 300px;
  padding: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #ffffff;
}

.donut-wrapper {
  position: relative;

  width: 170px;
  height: 170px;

  flex: 0 0 170px;

  margin: 0 auto;
}

.donut-wrapper>div:not(.donut-center) {
  position: relative !important;

  width: 100% !important;
  height: 100% !important;
}

::v-deep .donut-wrapper canvas {
  display: block !important;

  width: 100% !important;
  height: 100% !important;

  max-width: 100% !important;
  max-height: 100% !important;
}

.donut-center {
  position: absolute;
  inset: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;

  pointer-events: none;
}

.donut-center strong {
  color: #0f172a;
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.donut-center span {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 2px;
}

.donut-breakdown-row {
  border-color: #f1f5f9 !important;
}

.donut-breakdown-item {
  flex: 1;
}

.donut-breakdown-val {
  font-size: 1.1rem;
}

.donut-breakdown-divider {
  width: 1px;
  height: 28px;
  background: #e2e8f0;
}

/* =====================================================
   WORK
===================================================== */

.work-layout {
  width: 100%;
  min-width: 0;

  display: grid;

  grid-template-columns:
    minmax(0, 1.55fr) minmax(340px, 0.95fr);

  gap: 18px;

  margin-bottom: 18px;

  align-items: stretch;
}

.work-layout-full {
  grid-template-columns: minmax(0, 1fr);
}

.technician-workload-card {
  margin-bottom: 16px;
}

.technician-workload-wrapper {
  width: 100%;
  overflow-x: auto;
}

.technician-workload-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 680px;
}

.technician-workload-table th {
  background: #f1f5f9 !important;
  color: #0f172a !important;
  font-weight: 800 !important;
  padding: 12px 14px !important;
  font-size: 0.98rem !important; /* 15.6px - large & bold header */
  border-bottom: 2px solid #cbd5e1 !important;
  text-align: left;
  white-space: nowrap;
}

.technician-workload-table td {
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  font-size: 0.95rem !important; /* 15.2px - clear cell text */
  color: #1e293b !important;
  font-weight: 600 !important;
  vertical-align: middle;
}

.technician-workload-table td:not(:first-child),
.technician-workload-table th:not(:first-child) {
  text-align: center;
}

.technician-workload-table tbody tr:hover {
  background-color: #f8fafc;
}

.technician-workload-empty {
  color: var(--muted);
  text-align: center !important;
}

.supervisor-workload-card {
  margin-bottom: 16px;
}

.workload-role {
  display: inline-block;
  background: #e0f2fe !important;
  color: #0369a1 !important;
  padding: 4px 10px !important;
  border-radius: 6px !important;
  font-size: 0.85rem !important;
  font-weight: 800 !important;
}

.workload-section-text {
  color: #0f172a !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
}

.workload-section {
  display: block;
  margin-top: 2px;
  color: #475569;
  font-size: 0.82rem;
  overflow-wrap: anywhere;
}

.workload-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workload-progress span {
  flex: 1;
  height: 10px !important;
  background: #e2e8f0 !important;
  border-radius: 999px !important;
  overflow: hidden;
  display: block;
}

.workload-progress span i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0284c7 0%, #059669 100%) !important;
  transition: width 0.3s;
  border-radius: 999px !important;
}

.workload-progress b {
  min-width: 38px;
  color: #1e293b;
  font-size: 0.85rem;
  font-weight: 700;
  text-align: right;
}

.technician-availability {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.technician-availability.is-available,
.technician-availability.available,
.is-available {
  background: #e8f5e9;
  color: #2e7d32;
}

.technician-availability.is-busy,
.technician-availability.busy,
.is-busy {
  background: #fff3e0;
  color: #e65100;
}

.technician-current-tickets {
  display: block;
  width: 100%;
  max-width: 360px;
  overflow-wrap: anywhere;
  text-align: left;
}

.current-ticket-line {
  font-size: 0.8rem;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #1e293b;
}

.current-ticket-status {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
  white-space: nowrap;
}

.sla-normal {
  background: #e8f5e9;
  color: #2e7d32;
}

.sla-urgent {
  background: #fff8e1;
  color: #f57f17;
}

.sla-overdue {
  background: #ffebee;
  color: #c62828;
}

.sla-done {
  background: #e0e0e0;
  color: #616161;
}

.no-current-ticket {
  color: var(--muted);
  font-size: 0.82rem;
}

.todo-status-cell {
  display: flex;
  align-items: center;
}

.todo-status-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;

  .sla-countdown-badge {
    margin-top: 0 !important;
  }
}

.sla-countdown-badge,
.current-ticket-status {
  display: inline-flex;
  width: fit-content;
  margin-top: 4px;
  padding: 3px 7px;
  border-radius: 12px;
  font-size: 0.66rem;
  font-weight: 700;
  white-space: nowrap;
}

.sla-safe { color: var(--success); background: #f1f8f2; }
.sla-warning { color: #9a6a00; background: #fff8df; }
.sla-urgent,
.sla-overdue { color: var(--danger); background: #fdf2f2; }
.sla-neutral { color: var(--muted); background: #f5f5f5; }

.parts-overview-card { margin-bottom: 16px; }
.parts-overview-table th,
.parts-overview-table td {
  vertical-align: middle;
}
.parts-overview-table {
  min-width: 760px;
}
.parts-overview-table th,
.parts-overview-table td {
  text-align: left !important;
}
.parts-overview-table thead th:first-child,
.parts-overview-table tbody td:first-child {
  text-align: left !important;
}
.parts-overview-table thead th:nth-child(4),
.parts-overview-table tbody td:nth-child(4),
.parts-overview-table thead th:nth-child(5),
.parts-overview-table tbody td:nth-child(5) {
  text-align: center !important;
}
.parts-overview-table .no-current-ticket {
  display: inline-block;
  text-align: center;
}
/* =====================================================
   IMPECCABLE PARTS OVERVIEW TABLE & POPUP MODAL
===================================================== */
.parts-workload-table th {
  background: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  border-bottom: 2px solid #e2e8f0 !important;
  padding: 12px 16px !important;
}

.parts-workload-table td {
  padding: 12px 16px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 0.88rem !important;
}

.parts-inline-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.parts-inline-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 0.78rem;
  font-weight: 600;
}

.parts-more-count {
  display: inline-block;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.parts-qty-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-weight: 700;
  font-size: 0.82rem;
}

.parts-price-val {
  font-weight: 700;
  color: #64748b;
  font-size: 0.92rem;

  &.has-cost {
    color: #0f172a;
  }
}

.parts-detail-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border: 1px solid #bfdbfe !important;
  border-radius: 6px !important;
  color: #1d4ed8 !important;
  background: #eff6ff !important;
  font-size: 0.82rem !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: none !important;
  transition: all 0.15s ease !important;
  text-align: center;

  &:hover {
    background: #1d4ed8 !important;
    border-color: #1d4ed8 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(29, 78, 216, 0.2) !important;
  }
}

.parts-table-footer {
  padding: 14px 18px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
}

.parts-summary-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.parts-summary-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #000000 !important;

  .chip-label {
    color: #000000 !important;
    font-weight: 700;
  }
  .chip-value {
    color: #000000 !important;
    font-weight: 800;
  }

  &.parts-summary-total {
    background: #eff6ff;
    border-color: #93c5fd;

    .chip-label {
      color: #000000 !important;
      font-weight: 700;
    }
    .chip-value-total {
      color: #000000 !important;
      font-weight: 800;
      font-size: 1rem;
    }
    .chip-unit {
      color: #000000 !important;
      font-weight: 700;
    }
  }
}

/* MODAL STYLING - CLEAN EXECUTIVE HIGH-CONTRAST */
::v-deep .parts-detail-modal .modal-content {
  border: 1px solid #cbd5e1 !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16) !important;
}

::v-deep .parts-detail-modal .modal-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 20px 24px !important;
}

.parts-modal-title {
  color: #000000 !important;
  font-size: 1.25rem !important;
  font-weight: 800 !important;
  line-height: 1.3;
}

.parts-modal-subtitle {
  font-size: 0.88rem !important;
  color: #1e293b !important;
  font-weight: 500;
  line-height: 1.3;
}

.parts-modal-close-btn {
  background: transparent;
  border: none;
  font-size: 1.6rem;
  color: #64748b;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #000000;
  }
}

::v-deep .parts-detail-modal .modal-body {
  padding: 22px 24px !important;
  background: #ffffff !important;
}

::v-deep .parts-detail-modal .modal-footer {
  background: #f8fafc !important;
  border-top: 1px solid #e2e8f0 !important;
  padding: 14px 24px !important;
}

.parts-modal-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 14px 20px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  margin-bottom: 20px;
}

.parts-info-col {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.parts-info-label {
  font-size: 0.82rem !important;
  color: #1e293b !important;
  font-weight: 700 !important;
}

.parts-info-value {
  font-size: 1rem !important;
  color: #000000 !important;
  font-weight: 700 !important;

  &.ticket-id {
    color: #000000 !important;
    font-weight: 800 !important;
    font-size: 1.1rem !important;
  }
}

.parts-modal-table-container {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
}

.parts-modal-table {
  width: 100%;
  border-collapse: collapse;

  th {
    background: #f1f5f9 !important;
    color: #000000 !important;
    font-size: 0.88rem !important;
    font-weight: 800 !important;
    padding: 12px 16px !important;
    border-bottom: 2px solid #cbd5e1 !important;
  }

  td {
    padding: 12px 16px !important;
    font-size: 0.92rem !important;
    color: #000000 !important;
    font-weight: 600 !important;
    border-bottom: 1px solid #e2e8f0 !important;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .th-num, .td-num { width: 6%; text-align: center; color: #000000 !important; font-weight: 700 !important; }
  .th-name, .td-name { width: 44%; color: #000000 !important; font-weight: 700 !important; }
  .th-qty, .td-qty { width: 14%; color: #000000 !important; font-weight: 700 !important; }
  .th-price, .td-price { width: 18%; color: #000000 !important; font-weight: 600 !important; }
  .th-total, .td-total { width: 18%; color: #000000 !important; font-weight: 800 !important; }
}

.parts-modal-empty-state {
  padding: 36px 16px;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #000000 !important;
}

.parts-modal-summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8fafc;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  margin-top: 20px;
}

.parts-summary-title {
  color: #000000 !important;
  font-size: 1.05rem !important;
  font-weight: 800 !important;
}

.parts-summary-amount strong {
  font-size: 1.5rem !important;
  font-weight: 900 !important;
  color: #000000 !important;
}

.parts-currency-unit {
  font-size: 0.95rem !important;
  color: #000000 !important;
  font-weight: 700 !important;
  margin-left: 6px;
}

.parts-modal-close-action-btn {
  background: #dc2626 !important;
  border: 1px solid #b91c1c !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  padding: 8px 28px !important;
  border-radius: 8px !important;
  font-size: 0.95rem !important;
  cursor: pointer;
  transition: all 0.15s ease !important;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3) !important;

  &:hover {
    background: #b91c1c !important;
    border-color: #991b1b !important;
    box-shadow: 0 2px 6px rgba(185, 28, 28, 0.4) !important;
  }
}
::v-deep .report-download-modal .modal-header { color: #fff; background: var(--admin-primary); border-bottom-color: var(--admin-primary); }
::v-deep .report-download-modal .modal-title,
::v-deep .report-download-modal .modal-header .close { color: #fff !important; }
::v-deep .report-download-modal .modal-header .close { opacity: 0.85; text-shadow: none; }

.work-card {
  position: relative;

  width: 100%;
  max-width: 100%;
  min-width: 0;

  background: #ffffff;

  padding: 18px 20px;
  border: 1px solid var(--border-soft);
  border-radius: 12px;

  overflow: hidden;

  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.work-card-header {
  width: 100%;
  min-width: 0;
  margin-bottom: 12px;
}

.work-card-header>div {
  min-width: 0;
}

.work-card-header h4 {
  margin: 0;

  color: var(--admin-primary);
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.work-card-header p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.85rem;
}

.view-all-button {
  flex: 0 0 auto;

  padding: 0.25rem 0.5rem;

  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 3px;

  background: transparent;
  color: #ffffff;

  font-family: "Sarabun", sans-serif;
  font-size: 0.7rem;
  line-height: 1.5;

  white-space: nowrap;

  cursor: pointer;
}

.view-all-button:hover {
  color: var(--admin-primary);
  background: #ffffff;
}

/* =====================================================
   SUPERVISOR TO-DO & DETAIL MASTER-DETAIL LAYOUT
===================================================== */

.sup-work-layout {
  display: grid;
  grid-template-columns: 1.55fr 1fr;
  gap: 22px;
  width: 100%;
  max-width: 100%;
  align-items: stretch;
}

.sup-work-layout-full {
  grid-template-columns: 1fr;
}

.sup-work-card {
  position: relative;
  width: 100%;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px -2px rgba(2, 132, 199, 0.06);
  display: flex;
  flex-direction: column;
}

.sup-work-card-header {
  padding: 18px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;

  h4 {
    color: #0f172a;
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.2;
  }
}

.sup-header-sub {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.sup-todo-counter-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.sup-view-all-btn {
  font-size: 0.78rem;
  border-radius: 6px;
  border-color: #bfdbfe;
  color: #1d4ed8;
  background: #eff6ff;
  padding: 4px 10px;
  transition: all 0.15s ease;

  &:hover {
    background: #1d4ed8;
    color: #ffffff;
    border-color: #1d4ed8;
  }
}

/* =====================================================
   SUPERVISOR TASK TABLE
===================================================== */

.sup-task-table {
  width: 100%;
  min-width: 0;
  background: #ffffff;
}

.sup-task-table-header,
.sup-task-table-row {
  display: grid;
  grid-template-columns: 1.15fr 1.25fr 1.7fr 1.1fr 34px;
  align-items: center;
  gap: 12px;
}

.sup-task-table-header {
  padding: 10px 18px;
  color: #475569;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sup-task-table-row {
  padding: 12px 18px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.82rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  &.selected {
    background: #eff6ff !important;
    box-shadow: inset 3px 0 0 #2563eb;

    .sup-ticket-link {
      color: #1d4ed8;
      font-weight: 800;
    }

    .sup-row-chevron {
      background: #1d4ed8;
      color: #ffffff;
      border-color: #1d4ed8;
    }
  }

  &:last-child {
    border-bottom: 0;
  }
}

.sup-ticket-col {
  min-width: 0;
}

.sup-ticket-num {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1d4ed8;
  letter-spacing: -0.2px;
}

.sup-ticket-date {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 0.7rem;
}

.sup-reporter-col {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sup-reporter-avatar {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  font-size: 0.85rem;
}

.sup-reporter-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.sup-reporter-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sup-reporter-dept {
  font-size: 0.7rem;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sup-problem-col {
  min-width: 0;
}

.sup-problem-text {
  color: #334155;
  font-size: 0.82rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.sup-problem-loc {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
  color: #64748b;
  font-size: 0.7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sup-loc-icon {
  font-size: 0.68rem;
  flex-shrink: 0;
}

.sup-status-col {
  min-width: 0;
}

.sup-status-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.sup-action-col {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sup-row-chevron {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &.is-selected {
    background: #1d4ed8;
    color: #ffffff;
    border-color: #1d4ed8;
  }
}

.soft-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.soft-status-pending {
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fde68a;
}

.soft-status-active {
  color: #1d4ed8;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.task-empty {
  min-height: 230px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: #94a3b8;
  font-size: 0.82rem;
  text-align: center;
}

/* =====================================================
   TO-DO PAGINATION
===================================================== */

.todo-pagination {
  width: 100%;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  background: #ffffff;
  border-top: 1px solid #f1f5f9;
  box-sizing: border-box;
}

.todo-page-button {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  font-family: "Sarabun", sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled):not(.active) {
    color: #1d4ed8;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  &.active {
    color: #ffffff;
    background: #1d4ed8;
    border-color: #1d4ed8;
  }

  &:disabled {
    color: #cbd5e1;
    background: #f8fafc;
    cursor: not-allowed;
  }
}

/* =====================================================
   SUPERVISOR SELECTED DETAIL CARD (ZERO GIGANTIC BLUE HEADER)
===================================================== */

.sup-detail-card {
  min-height: 100%;
  border-top: 4px solid #1d4ed8 !important;
  background: #ffffff !important;
}

.sup-detail-header {
  padding: 18px 24px !important;
  background: #ffffff !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.sup-detail-eyebrow {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.sup-detail-ticket-id {
  color: #0f172a !important;
  font-size: 1.25rem !important;
  font-weight: 800 !important;
  margin: 2px 0 0 !important;
  letter-spacing: -0.3px;
}

.sup-detail-body {
  flex: 1;
  padding: 20px 24px;
  background: #ffffff;
}

.sup-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.sup-meta-box {
  min-width: 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.sup-meta-label {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sup-meta-val {
  margin-top: 4px;
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.sup-problem-box {
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #3b82f6;
  border-radius: 8px;
  background: #f8fafc;
}

.sup-problem-box-title {
  display: block;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.sup-problem-box-desc {
  margin: 0;
  color: #334155;
  font-size: 0.84rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.sup-detail-header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.sup-close-detail-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #dc2626;
  }
}

.sup-detail-footer {
  padding: 14px 20px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.sup-assign-btn {
  padding: 10px 18px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #1d4ed8;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #1e40af;
    color: #ffffff;
  }
}

/* =====================================================
   MOBILE TO-DO LIST REDESIGN (IMPECCABLE)
====================================================== */
.sup-mobile-todo-section {
  width: 100%;
}

.sup-mobile-todo-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.sup-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 99px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  .chip-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 99px;
    font-size: 0.72rem;
    font-weight: 700;
    background: #f1f5f9;
    color: #475569;
  }

  &.active {
    background: #eff6ff;
    border-color: #2563eb;
    color: #1d4ed8;

    .chip-count {
      background: #2563eb;
      color: #ffffff;
    }
  }
}

.sup-mobile-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.sup-mobile-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  cursor: pointer;
  transition: all 0.15s ease;

  &:active {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &.is-selected {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
}

.sup-m-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sup-m-ticket-group {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sup-m-ticket-num {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.2px;
}

.sup-m-ticket-date {
  font-size: 0.75rem;
  color: #64748b;
}

.sup-m-problem-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sup-m-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.sup-m-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #475569;
  font-weight: 500;

  .sup-m-chip-icon {
    width: 12px;
    height: 12px;
    color: #64748b;
  }
}

.sup-m-footer {
  margin-top: 2px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.sup-m-assign-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover, &:active {
    background: #dbeafe;
    color: #1e40af;
  }
}

/* =====================================================
   REPORT TABLE
===================================================== */

.report-table-card {
  width: 100%;
  min-width: 0;

  margin-bottom: 24px;
}

.report-table-wrapper {
  width: 100%;
  min-width: 0;

  padding: 18px;

  overflow-x: auto;
  overflow-y: visible;

  -webkit-overflow-scrolling: touch;

  background: #ffffff;

  box-sizing: border-box;
}

.print-btn {
  flex: 0 0 auto;

  border: 1px solid rgba(255, 255, 255, 0.85) !important;

  color: #ffffff !important;
  background: transparent !important;

  font-family: "Sarabun", sans-serif;
  font-size: 0.75rem;

  box-shadow: none !important;
}

.print-btn:hover,
.print-btn:focus {
  color: var(--admin-primary) !important;

  background: #ffffff !important;
  border-color: #ffffff !important;

  box-shadow: none !important;
}

/* =====================================================
   DATATABLE
===================================================== */

::v-deep .table {
  width: 100%;
  min-width: 850px;

  margin-bottom: 0;

  border-collapse: collapse;

  background: #ffffff;

  font-family: "Sarabun", sans-serif;
}

::v-deep .table thead th {
  padding: 10px;

  vertical-align: middle;

  border-top: 0;
  border-bottom: 1px solid #dee2e6;

  background: #f8f9fa;
  color: #666666;

  font-size: 0.75rem;
  font-weight: 700;

  white-space: nowrap;
}

::v-deep .table tbody td {
  padding: 10px;

  vertical-align: middle;

  border-top: 1px solid var(--border-soft);

  background: #ffffff;
  color: #444444;

  font-size: 0.8rem;

  white-space: nowrap;
}

::v-deep .table tbody tr:hover td {
  background: #fbf8f8;
}

/* =====================================================
   DATATABLE CONTROLS
===================================================== */

::v-deep .form-inline,
::v-deep .form-inline label,
::v-deep .c-datatable-filter,
::v-deep .c-datatable-filter label {
  font-family: "Sarabun", sans-serif;
  font-size: 0.8rem;
}

::v-deep .c-datatable>.row:first-child {
  row-gap: 8px;
}

::v-deep .form-inline {
  margin-bottom: 8px;
}

::v-deep .form-inline label,
::v-deep .c-datatable-filter label {
  margin-right: 6px;
}

::v-deep .form-control,
::v-deep .custom-select {
  min-height: 32px;

  border: 1px solid #dee2e6;
  border-radius: 4px;

  background: #ffffff;

  font-family: "Sarabun", sans-serif;
  font-size: 0.75rem;

  box-shadow: none;
}

::v-deep .form-control:focus,
::v-deep .custom-select:focus {
  border-color: var(--admin-primary);

  box-shadow:
    0 0 0 2px rgba(122, 27, 28, 0.08);
}

/* =====================================================
   BADGE
===================================================== */

::v-deep .badge {
  font-family: "Sarabun", sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
}

/* =====================================================
   PAGINATION
===================================================== */

::v-deep .pagination {
  margin-top: 14px;
  margin-bottom: 0;

  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 6px;
}



/* =====================================================
   MODAL
===================================================== */

::v-deep .modal-content {
  overflow: hidden;

  border: 0;
  border-radius: 6px;

  font-family: "Sarabun", sans-serif;
}

::v-deep .modal-header {
  color: #ffffff;

  background: var(--admin-primary);

  border-bottom: 0;
}

::v-deep .modal-title {
  color: #ffffff !important;

  font-size: 1.1rem;
  font-weight: 700;
}

::v-deep .modal-body,
::v-deep .modal-footer,
::v-deep .modal-body label {
  font-family: "Sarabun", sans-serif;
  font-size: 0.875rem;
}

/* =====================================================
   <= 1400
===================================================== */

@media (max-width: 1400px) {
  .kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  }
}

/* =====================================================
   <= 1200
===================================================== */

@media (max-width: 1200px) {

  .analytics-layout,
  .work-layout {
    grid-template-columns:
      minmax(0, 1fr);
  }

  .analytics-side {
    display: block;
  }

  .operational-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .overview-panel-secondary {
    grid-column: 1 / -1;
    flex-direction: row;
    justify-content: flex-start;
    gap: 18px;
  }
}

/* =====================================================
   TABLET <= 768
===================================================== */

@media (max-width: 768px) {
  .supervisor-dashboard {
    padding: 4px 0 20px !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 12px 0 !important;
    padding: 0 !important;
    background: transparent !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  .dashboard-topbar {
    margin-bottom: 0 !important;
    padding: 10px 12px;

    flex-direction: column;
    align-items: flex-start;

    gap: 10px;
    border-radius: 8px;
  }

  .dashboard-page-title {
    font-size: 1.15rem;
    gap: 8px;
  }

  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 20px;
    height: 20px;
    font-size: 1.2rem;
  }

  .dashboard-filter {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 4px;
  }

  .range-tabs {
    flex-wrap: nowrap;
    width: auto;
    gap: 6px;
    overflow: visible;
    border: 0;
  }

  .range-tab {
    min-height: 34px;
    padding: 6px 14px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-right: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    font-size: 0.85rem;
  }

  .filter-tab {
    width: 36px;
    min-width: 36px;
  }

  .custom-date-range {
    width: 100%;
  }

  .custom-date-range input {
    width: auto;
    min-width: 0;

    flex: 1 1 0;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
    margin-top: 0 !important;
    margin-bottom: 12px !important;
    padding: 0;
  }

  .operational-summary {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 12px;
  }

  .overview-panel-secondary {
    grid-column: auto;
  }

  .summary-card {
    height: auto;
    padding: 14px 16px;
  }

  .summary-icon {
    width: 34px;
    height: 34px;

    flex-basis: 34px;
  }

  .summary-label {
    margin-bottom: 4px;
    font-size: 0.85rem;
    letter-spacing: 0;
  }

  .summary-value {
    font-size: 1.8rem;
  }

  .summary-value small {
    font-size: 13px;
  }

  .analytics-layout {
    margin-bottom: 12px;
  }

  .analytics-card-header,
  .work-card-header {
    min-height: 0;
    padding: 0;
    margin-bottom: 12px;
  }

  .analytics-card-header h4,
  .work-card-header h4 {
    font-size: 1rem;
  }

  .analytics-card-header p,
  .work-card-header p {
    font-size: 0.65rem;
  }

  .analytics-side {
    display: flex;
    flex-direction: column;
  }

  .mini-stats {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .highlight-chart-wrapper {
    height: 260px;

    padding: 14px 8px 10px;
  }

  .performance-content {
    min-height: 0;

    padding: 18px 14px;

    flex-direction: column;
    align-items: stretch;
  }





  .donut-wrapper {
    width: 180px;
    height: 180px;

    flex-basis: 180px;

    margin: 0 auto;
  }

  .technician-workload-wrapper {
    overflow-x: auto;
    padding: 10px;
  }

  .technician-workload-table {
    min-width: 0;
  }

  .technician-workload-table thead {
    display: none;
  }

  .technician-workload-table,
  .technician-workload-table tbody,
  .technician-workload-table tr,
  .technician-workload-table td {
    display: block;
    width: 100%;
  }

  .technician-workload-table tr {
    margin-bottom: 10px;
    padding: 8px 10px;
    border: 1px solid var(--border-soft);
    border-radius: 6px;
  }

  .technician-workload-table td {
    min-width: 0;
    padding: 7px 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    text-align: right !important;
  }

  .technician-workload-table td::before {
    content: attr(data-label);
    flex: 0 0 42%;
    color: var(--muted);
    font-size: 0.7rem;
    font-weight: 700;
    text-align: left;
  }

  .technician-workload-table .technician-workload-empty {
    display: block;
    text-align: center !important;
  }

  .technician-workload-table .technician-workload-empty::before {
    display: none;
  }

  .parts-overview-table tfoot,
  .parts-overview-table tfoot tr,
  .parts-overview-table tfoot td {
    display: block;
    width: 100%;
  }

  .parts-summary-total-row {
    margin-top: 12px;
    border: 2px solid var(--admin-primary) !important;
    border-radius: 8px;
    background: #eff6ff;
    overflow: hidden;
  }

  .parts-summary-total-row td {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 10px 12px !important;
    border-bottom: 1px dashed rgba(37, 99, 235, 0.15) !important;
    text-align: right !important;
  }

  .parts-summary-total-row td:last-child {
    display: none !important;
  }

  .parts-table-items {
    width: 100%;
  }

  .parts-table-item-line {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .parts-detail-values {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 5px;
  }

  .parts-detail-button {
    width: 100%;
  }

  .work-layout,
  .sup-work-layout {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 14px;
  }

  /* =====================================================
   MOBILE TO-DO LIST
===================================================== */

  .work-card:first-child {
    overflow: hidden;
  }

  .task-table {
    width: 100%;
    min-width: 0;

    padding: 10px;

    background: #ffffff;
  }

  /* ซ่อนหัวตารางบนมือถือ */
  .task-table-header {
    display: none;
  }

  /* 1 Ticket = 1 Card */
  .task-table-row {
    width: 100%;

    margin-bottom: 10px;
    padding: 12px;

    display: grid;

    grid-template-columns: 1fr auto;
    grid-template-areas:
      "ticket action"
      "reporter reporter"
      "description description"
      "status status";

    gap: 8px 10px;

    align-items: center;

    background: #ffffff;

    border: 1px solid var(--border-soft);
    border-radius: 6px;

    box-shadow:
      0 2px 7px rgba(0, 0, 0, 0.035);

    box-sizing: border-box;
  }

  /* เอาเส้นของ row desktop ออก */
  .task-table-row:last-child {
    margin-bottom: 0;
  }

  .task-table-row:hover {
    background: #fbf8f8;
  }

  /* Ticket */
  .task-table-row>div:nth-child(1) {
    grid-area: ticket;

    min-width: 0;
  }

  /* Reporter */
  .task-table-row>div:nth-child(2) {
    grid-area: reporter;

    min-width: 0;
  }

  /* Description */
  .task-table-row>div:nth-child(3) {
    grid-area: description;

    min-width: 0;

    padding-top: 8px;

    border-top: 1px solid var(--border-soft);
  }

  /* Status */
  .task-table-row>div:nth-child(4) {
    grid-area: status;

    min-width: 0;

    padding-top: 8px;

    border-top: 1px solid var(--border-soft);
  }

  /* Action */
  .task-table-row>div:nth-child(5) {
    grid-area: action;

    justify-self: end;
  }

  /* Ticket number */
  .ticket-number {
    font-size: 0.85rem;
    font-weight: 700;

    color: var(--text);
  }

  /* วันที่ใต้ Ticket */
  .task-table-row small {
    margin-top: 3px;

    font-size: 0.68rem;

    color: #999999;
  }

  /* Reporter */
  .reporter-cell {
    width: 100%;

    display: flex;
    align-items: center;

    gap: 8px;
  }

  .reporter-avatar {
    width: 31px;
    height: 31px;

    flex: 0 0 31px;
  }

  /* Description ต้องไม่ล้นออกขวา */
  .task-description {
    width: 100%;
    min-width: 0;

    overflow: visible;

    color: #666666;

    white-space: normal;

    text-overflow: unset;
    overflow-wrap: anywhere;
    word-break: break-word;

    line-height: 1.45;
  }

  /* Status */
  .soft-status {
    width: fit-content;

    white-space: nowrap;
  }

  /* ปุ่ม ... */
  .row-action {
    width: 28px;
    height: 28px;

    flex: 0 0 28px;
  }

  .detail-grid {
    grid-template-columns:
      minmax(0, 1fr);
  }

  .report-table-wrapper {
    padding: 10px;
  }
}

/* =====================================================
   MOBILE <= 576
===================================================== */

@media (max-width: 576px) {
  .supervisor-dashboard {
    padding: 4px 0 85px !important;
  }

  /* -----------------------------
     HEADER
  ----------------------------- */

  .dashboard-topbar {
    margin-bottom: 0;

    padding: 9px 10px;

    gap: 9px;

    border-radius: 8px;
  }

  .dashboard-page-title {
    font-size: 1.05rem;
    gap: 6px;
  }

  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 18px;
    height: 18px;
  }

  .range-tabs {
    justify-content: flex-start;
    width: auto;
    gap: 6px;
    overflow: visible;
    border: 0;
  }

  .range-tab {
    flex: 0 0 auto;

    min-height: 32px;

    padding: 6px 14px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-right: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    font-size: 0.85rem;
  }

  .filter-tab {
    width: 34px;
    min-width: 34px;

    padding: 0;
  }

  /* -----------------------------
     KPI
  ----------------------------- */

  .kpi-grid {
    padding: 0 !important;
    gap: 10px !important;
  }

  .summary-card {
    height: auto !important;
    min-height: 122px !important;
    padding: 12px 14px !important;
    overflow: visible !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    border-radius: 14px !important;
    background: #ffffff !important;
    border: 1px solid #dbeafe !important;
  }

  .summary-icon {
    width: 34px !important;
    height: 34px !important;
    flex: 0 0 34px !important;
    border-radius: 10px !important;
  }

  .summary-label {
    margin-top: 4px !important;
    margin-bottom: 4px !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    line-height: 1.25 !important;
    letter-spacing: normal !important;
    color: #475569 !important;
  }

  .summary-bottom {
    display: flex !important;
    align-items: baseline !important;
    justify-content: space-between !important;
    margin-top: auto !important;
    padding-top: 4px !important;
  }

  .summary-value {
    font-size: 1.65rem !important;
    line-height: 1.15 !important;
    font-weight: 800 !important;
    color: #0f172a !important;
  }

  .summary-value small {
    font-size: 13px !important;
  }

  /* -----------------------------
     ANALYTICS
  ----------------------------- */

  .analytics-card-header,
  .work-card-header {
    min-height: 50px;

    padding: 10px 12px;
  }

  .analytics-card-header h4,
  .work-card-header h4 {
    font-size: 1rem;
  }

  .analytics-card-header p,
  .work-card-header p {
    font-size: 0.65rem;
  }

  .highlight-chart-wrapper {
    height: 270px;

    padding:
      12px 4px 8px;
  }

  .donut-wrapper {
    width: 165px;
    height: 165px;

    flex-basis: 165px;
  }

  .donut-center strong {
    font-size: 26px;
  }

  /* -----------------------------
     TO-DO PAGINATION
  ----------------------------- */

  .todo-pagination {
    padding: 12px 10px;
    justify-content: center;
    gap: 5px;
  }

  .todo-page-button {
    min-width: 35px;
    height: 35px;
    padding: 0 9px;
    font-size: 0.72rem;
  }

  /* -----------------------------
     TICKET HEADER
  ----------------------------- */

  .report-table-card {
    margin-bottom: 14px;
  }

  .report-table-card .work-card-header {
    flex-wrap: nowrap !important;
    align-items: center !important;

    gap: 8px;
  }

  .report-table-card .work-card-header h4 {
    min-width: 0;

    flex: 1 1 auto;

    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .print-btn {
    flex: 0 0 auto;

    margin-left: 8px !important;

    white-space: nowrap;
  }

  /* -----------------------------
     TICKET WRAPPER
  ----------------------------- */

  .report-table-wrapper {
    width: 100%;

    padding: 10px;

    overflow-x: hidden !important;
  }

  /* -----------------------------
     FILTER / ITEMS PER PAGE
  ----------------------------- */

  ::v-deep .c-datatable>.row:first-child {
    display: flex !important;

    flex-direction: column !important;
    align-items: flex-start !important;

    gap: 8px !important;
  }

  ::v-deep .c-datatable>.row:first-child>div {
    width: 100% !important;
    max-width: 100% !important;

    flex: 0 0 100% !important;
  }

  ::v-deep .form-inline,
  ::v-deep .c-datatable-filter {
    display: flex !important;

    width: 100% !important;

    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center !important;

    gap: 6px;

    margin: 0 0 8px !important;
  }

  ::v-deep .form-inline label,
  ::v-deep .c-datatable-filter label {
    flex: 0 0 auto;

    margin-right: 4px !important;
    margin-bottom: 0 !important;

    font-size: 0.75rem !important;

    white-space: nowrap !important;
  }

  ::v-deep .form-inline .form-control,
  ::v-deep .c-datatable-filter .form-control {
    width: auto !important;
    max-width: 145px !important;

    flex: 1 1 100px !important;

    min-height: 32px;

    font-size: 0.72rem !important;
  }

  ::v-deep .form-inline select.form-control,
  ::v-deep .form-inline select.custom-select,
  ::v-deep .form-inline .custom-select {
    width: 70px !important;
    max-width: 70px !important;

    flex: 0 0 70px !important;
  }

  /* =====================================================
     MOBILE TABLE -> CARDS
  ===================================================== */

  ::v-deep .table {
    width: 100% !important;
    min-width: 0 !important;

    display: block !important;

    margin-top: 6px;

    border: 0;
  }

  ::v-deep .table thead {
    display: none !important;
  }

  ::v-deep .table tbody {
    width: 100% !important;

    display: block !important;
  }

  ::v-deep .table tbody tr {
    width: 100% !important;

    display: block !important;

    margin: 0 0 10px;
    padding: 10px 12px;

    background: #ffffff;

    border: 1px solid var(--border-soft);
    border-radius: 6px;

    box-shadow:
      0 2px 7px rgba(0, 0, 0, 0.035);

    box-sizing: border-box;
  }

  ::v-deep .table tbody tr:last-child {
    margin-bottom: 0;
  }

  ::v-deep .table tbody tr:hover {
    background: #ffffff;
  }

  ::v-deep .table tbody td {
    width: 100% !important;
    min-width: 0 !important;

    padding: 7px 0 !important;

    display: flex !important;
    align-items: flex-start;
    justify-content: space-between;

    gap: 12px;

    border: 0 !important;
    border-bottom:
      1px solid var(--border-soft) !important;

    background: #ffffff !important;
    color: #444444;

    font-size: 0.75rem;

    white-space: normal !important;

    overflow-wrap: anywhere;

    box-sizing: border-box;
  }

  ::v-deep .table tbody td:last-child {
    border-bottom: 0 !important;
  }

  ::v-deep .table tbody td::before {
    flex: 0 0 40%;

    color: var(--muted);

    font-family: "Sarabun", sans-serif;
    font-size: 0.7rem;
    font-weight: 700;

    line-height: 1.4;

    text-align: left;
  }

  ::v-deep .table tbody td:nth-child(1)::before {
    content: "เลขที่ใบแจ้งซ่อม";
  }

  ::v-deep .table tbody td:nth-child(2)::before {
    content: "วันที่แจ้ง";
  }

  ::v-deep .table tbody td:nth-child(3)::before {
    content: "หน่วยงาน";
  }

  ::v-deep .table tbody td:nth-child(4)::before {
    content: "ผู้แจ้ง";
  }

  ::v-deep .table tbody td:nth-child(5)::before {
    content: "ประเภทงาน";
  }

  ::v-deep .table tbody td:nth-child(6)::before {
    content: "สถานะ";
  }

  ::v-deep .table tbody td:nth-child(7)::before {
    content: "SLA";
  }

  ::v-deep .table tbody td:nth-child(8)::before {
    content: "ผู้รับผิดชอบ";
  }

  ::v-deep .table tbody td:nth-child(9)::before {
    content: "วันที่ปิดงาน";
  }

  /* Badge ภายใน mobile card */

  ::v-deep .table tbody td .badge {
    flex: 0 0 auto;
  }

  ::v-deep .table tbody td small {
    text-align: right;
  }


}

/* =====================================================
   SMALL MOBILE <= 480
===================================================== */

@media (max-width: 480px) {

  .kpi-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .mini-stats {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .kpi-grid>.summary-card:last-child:nth-child(odd),
  .mini-stats>.summary-card:last-child:nth-child(odd) {
    grid-column: 1 / -1;
    width: 100%;
  }

  .dashboard-page-title {
    font-size: 1rem;
    gap: 6px;
  }

  .range-tab {
    min-height: 31px;

    padding: 5px 8px;

    font-size: 0.68rem;
  }

  .filter-tab {
    width: 32px;
    min-width: 32px;
  }

  .highlight-chart-wrapper {
    height: 250px;
  }

  .donut-wrapper {
    width: 160px;
    height: 160px;

    flex-basis: 160px;
  }

  ::v-deep .table tbody td::before {
    flex-basis: 42%;
  }
}

/* =====================================================
   VERY SMALL <= 360
===================================================== */

@media (max-width: 360px) {
  .kpi-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .summary-card {
    height: auto !important;
    min-height: 120px !important;
  }

  .range-tab {
    padding: 5px 8px;
  }

  .highlight-chart-wrapper {
    height: 240px;
  }

  ::v-deep .table tbody tr {
    padding: 9px 10px;
  }

  ::v-deep .table tbody td {
    gap: 8px;

    font-size: 0.7rem;
  }

  ::v-deep .table tbody td::before {
    flex-basis: 43%;

    font-size: 0.68rem;
  }
}

/* =====================================================
   PRINT
===================================================== */

@media print {

  .c-sidebar,
  .c-header,
  .c-footer,
  .dashboard-topbar,
  .kpi-grid,
  .analytics-layout,
  .work-layout,
  .print-btn {
    display: none !important;
  }

  .supervisor-dashboard {
    padding: 0;

    background: #ffffff;
  }

  .report-table-card {
    border: 0;

    box-shadow: none;
  }

  .report-table-wrapper {
    overflow: visible;
  }

  .c-wrapper {
    margin-left: 0 !important;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

</style>
<style scoped>
/* Top 5 Most Requested Repairs & Hot Key Styles */
.top5-chart-wrapper {
  background: #ffffff;
  padding: 16px 20px;
  min-height: 260px;
}
.top5-items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.top5-item-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.top5-item-row:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
.top5-rank-col {
  flex-shrink: 0;
  min-width: 44px;
}
.top5-rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 800;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
}
.top5-rank-badge.rank-1 {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}
.top5-rank-badge.rank-2 {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}
.top5-rank-badge.rank-3 {
  background: #ffedd5;
  color: #c2410c;
  border: 1px solid #fed7aa;
}
.top5-icon-col {
  flex-shrink: 0;
}
.top5-icon-tile {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e40af;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.top5-info-col {
  flex: 1;
  min-width: 0;
}
.top5-title-group {
  display: flex;
  align-items: center;
  min-width: 0;
}
.top5-name {
  font-size: 0.92rem;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.top5-counts {
  font-size: 0.88rem;
  white-space: nowrap;
}
.top5-bar-track {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}
.top5-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: #3b82f6;
  transition: width 0.4s ease-in-out;
}
.top5-bar-fill.fill-rank-1 {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}
.top5-bar-fill.fill-rank-2 {
  background: linear-gradient(90deg, #3b82f6, #2563eb);
}
.top5-bar-fill.fill-rank-3 {
  background: linear-gradient(90deg, #06b6d4, #0284c7);
}
.top5-action-col {
  flex-shrink: 0;
}
.hotkey-action-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1e40af;
  background: #ffffff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.hotkey-action-pill:hover,
.hotkey-action-pill.active {
  background: #1e40af;
  color: #ffffff;
  border-color: #1e40af;
}
.hotkey-action-pill.active .text-warning {
  color: #fde047 !important;
}
.active-hotkey-notice {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 8px 14px;
}
</style>
